import type { TemplateEntry } from '@cli-types/next-adapter/commands.js'
import { detectProjectName } from '@next-adapter/config/detect/detect-project-name.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { adminDataTemplate } from '@next-adapter/init/templates/app/admin.js'
import { renderCoreEmailProviderIndex } from '@next-adapter/integration-runtime/render-core-email-provider-index.js'
import { renderCoreEmailProvider } from '@next-adapter/integration-runtime/render-core-email-provider.js'
import { renderCoreStorageProviderIndex } from '@next-adapter/integration-runtime/render-core-storage-provider-index.js'
import { renderCoreStorageProvider } from '@next-adapter/integration-runtime/render-core-storage-provider.js'
import { readIntegrationTemplate } from '@next-adapter/integration-template-reader/read-integration-template.js'
import { readTemplate } from '@next-adapter/template-reader/read-template.js'
export const TEMPLATE_REGISTRY: Record<string, TemplateEntry> = {
  'storage-decode-storage-key': {
    relPath: 'utils/storage/decode-storage-key.ts',
    content: () => readTemplate('utils/storage/decode-storage-key.ts')
  },
  'storage-storage-error': {
    relPath: 'utils/storage/storage-error.ts',
    content: () => readTemplate('utils/storage/storage-error.ts')
  },
  'auth-auth': {
    relPath: 'lib/actions/auth/auth.ts',
    content: () => readTemplate('lib/actions/auth/auth.ts')
  },
  'auth-middleware': {
    relPath: 'lib/actions/auth/middleware.ts',
    content: () => readTemplate('lib/actions/auth/middleware.ts')
  },
  'email-form-email-delivery-input-2': {
    relPath: 'lib/actions/email/form-email-delivery-input.ts',
    content: () => readTemplate('lib/actions/email/form-email-delivery-input.ts')
  },
  'media-clamp-media-page-size': {
    relPath: 'lib/actions/media/clamp-media-page-size.ts',
    content: () => readTemplate('lib/actions/media/clamp-media-page-size.ts')
  },
  'media-clamp-media-page': {
    relPath: 'lib/actions/media/clamp-media-page.ts',
    content: () => readTemplate('lib/actions/media/clamp-media-page.ts')
  },
  'users-get-cached-users': {
    relPath: 'lib/actions/users/get-cached-users.ts',
    content: () => readTemplate('lib/actions/users/get-cached-users.ts')
  },
  'admin-globals': {
    relPath: 'admin-globals.css',
    content: () => readTemplate('admin-globals.css')
  },
  'admin-providers': {
    relPath: 'components/layouts/admin-providers.tsx',
    content: () => readTemplate('components/layouts/admin-providers.tsx')
  },
  'admin-nav-link': {
    relPath: 'components/layouts/admin-nav-link.tsx',
    content: () => readTemplate('components/layouts/admin-nav-link.tsx')
  },
  'admin-sidebar': {
    relPath: 'components/layouts/admin-sidebar.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar.tsx'),
    dependencies: [
      'admin-sidebar-branding',
      'admin-sidebar-branding-skeleton',
      'admin-sidebar-nav-link',
      'admin-sidebar-user-menu',
      'admin-sidebar-user-menu-skeleton',
      'sidebar-utils'
    ]
  },
  'admin-sidebar-branding': {
    relPath: 'components/layouts/admin-sidebar-branding.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-branding.tsx'),
    dependencies: ['sidebar-branding']
  },
  'admin-sidebar-branding-skeleton': {
    relPath: 'components/layouts/admin-sidebar-branding-skeleton.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-branding-skeleton.tsx')
  },
  'admin-sidebar-nav-link': {
    relPath: 'components/layouts/admin-sidebar-nav-link.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-nav-link.tsx'),
    dependencies: ['admin-nav-link', 'admin-sidebar-nav-link-skeleton']
  },
  'admin-sidebar-nav-link-skeleton': {
    relPath: 'components/layouts/admin-sidebar-nav-link-skeleton.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-nav-link-skeleton.tsx')
  },
  'admin-settings-sidebar': {
    relPath: 'components/layouts/admin-settings-sidebar.tsx',
    content: () => readTemplate('components/layouts/admin-settings-sidebar.tsx'),
    dependencies: ['admin-sidebar-nav-link']
  },
  'admin-sidebar-user-menu': {
    relPath: 'components/layouts/admin-sidebar-user-menu.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-user-menu.tsx'),
    dependencies: ['admin-sign-out']
  },
  'admin-sidebar-user-menu-skeleton': {
    relPath: 'components/layouts/admin-sidebar-user-menu-skeleton.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-user-menu-skeleton.tsx')
  },
  'sidebar-branding': {
    relPath: 'components/layouts/sidebar-branding.tsx',
    content: () => readTemplate('components/layouts/sidebar-branding.tsx'),
    dependencies: ['app-admin', 'admin-sidebar-branding-skeleton']
  },
  'admin-header': {
    relPath: 'components/layouts/admin-header.tsx',
    content: () => readTemplate('components/layouts/admin-header.tsx')
  },
  'admin-sign-out': {
    relPath: 'components/layouts/admin-sign-out.tsx',
    content: () => readTemplate('components/layouts/admin-sign-out.tsx')
  },
  'content-skeleton': {
    relPath: 'components/layouts/content-skeleton.tsx',
    content: () => readTemplate('components/layouts/content-skeleton.tsx')
  },
  'role-gate': {
    relPath: 'components/layouts/role-gate.tsx',
    content: () => readTemplate('components/layouts/role-gate.tsx'),
    dependencies: ['auth-session', 'auth-types']
  },
  'page-header': {
    relPath: 'components/shared/page-header.tsx',
    content: () => readTemplate('components/shared/page-header.tsx')
  },
  'sort-indicator': {
    relPath: 'components/shared/sort-indicator.tsx',
    content: () => readTemplate('components/shared/sort-indicator.tsx')
  },
  'dev-mode-integrate': {
    relPath: 'components/shared/dev-mode-integrate.tsx',
    content: () => readTemplate('components/shared/dev-mode-integrate.tsx'),
    dependencies: [
      'dev-mode-copyable-code-block',
      'dev-mode-lifecycle-hooks-tab',
      'dev-mode-snippets-tab',
      'dev-mode-types',
      'use-dev-mode-integration'
    ]
  },
  'dev-mode-copyable-code-block': {
    relPath: 'components/shared/dev-mode/copyable-code-block.tsx',
    content: () => readTemplate('components/shared/dev-mode/copyable-code-block.tsx'),
    dependencies: [
      'dev-mode-code-mirror',
      'dev-mode-plain-code-fallback',
      'dev-mode-types',
      'tabs',
      'use-copy-to-clipboard'
    ]
  },
  'dev-mode-code-mirror': {
    relPath: 'components/shared/dev-mode/dev-mode-code-mirror.tsx',
    content: () => readTemplate('components/shared/dev-mode/dev-mode-code-mirror.tsx'),
    dependencies: ['dev-mode-types']
  },
  'dev-mode-types': {
    relPath: 'components/shared/dev-mode/dev-mode-types.ts',
    content: () => readTemplate('components/shared/dev-mode/dev-mode-types.ts')
  },
  'dev-mode-plain-code-fallback': {
    relPath: 'components/shared/dev-mode/plain-code-fallback.tsx',
    content: () => readTemplate('components/shared/dev-mode/plain-code-fallback.tsx')
  },
  'dev-mode-lifecycle-hooks-tab': {
    relPath: 'components/shared/dev-mode/lifecycle-hooks-tab.tsx',
    content: () => readTemplate('components/shared/dev-mode/lifecycle-hooks-tab.tsx'),
    dependencies: [
      'accordion',
      'badge',
      'dev-mode-code-block-height-utils',
      'dev-mode-copyable-code-block',
      'dev-mode-types'
    ]
  },
  'dev-mode-snippets-tab': {
    relPath: 'components/shared/dev-mode/snippets-tab.tsx',
    content: () => readTemplate('components/shared/dev-mode/snippets-tab.tsx'),
    dependencies: [
      'accordion',
      'badge',
      'dev-mode-code-block-height-utils',
      'dev-mode-copyable-code-block',
      'dev-mode-types'
    ]
  },
  'delete-dialog': {
    relPath: 'components/shared/delete-dialog.tsx',
    content: () => readTemplate('components/shared/delete-dialog.tsx')
  },
  'entity-metadata': {
    relPath: 'components/shared/entity-metadata.tsx',
    content: () => readTemplate('components/shared/entity-metadata.tsx'),
    dependencies: ['entity-versions-button']
  },
  'entity-versions-button': {
    relPath: 'components/shared/entity-versions/entity-versions-button.tsx',
    content: () => readTemplate('components/shared/entity-versions/entity-versions-button.tsx'),
    dependencies: ['button', 'entity-versions-drawer', 'use-entity-versions']
  },
  'entity-versions-drawer': {
    relPath: 'components/shared/entity-versions/entity-versions-drawer.tsx',
    content: () => readTemplate('components/shared/entity-versions/entity-versions-drawer.tsx'),
    dependencies: ['drawer', 'entity-versions-current-row', 'entity-version-item', 'scroll-area']
  },
  'entity-versions-current-row': {
    relPath: 'components/shared/entity-versions/entity-versions-current-row.tsx',
    content: () =>
      readTemplate('components/shared/entity-versions/entity-versions-current-row.tsx'),
    dependencies: ['avatar', 'badge', 'tooltip', 'user-utils']
  },
  'entity-version-item': {
    relPath: 'components/shared/entity-versions/entity-version-item.tsx',
    content: () => readTemplate('components/shared/entity-versions/entity-version-item.tsx'),
    dependencies: ['avatar', 'badge', 'entity-version-restore-dialog', 'tooltip', 'user-utils']
  },
  'entity-version-restore-dialog': {
    relPath: 'components/shared/entity-versions/entity-version-restore-dialog.tsx',
    content: () =>
      readTemplate('components/shared/entity-versions/entity-version-restore-dialog.tsx'),
    dependencies: ['alert-dialog', 'button']
  },
  'entity-filters-bar': {
    relPath: 'components/shared/entity-filters-bar.tsx',
    content: () => readTemplate('components/shared/entity-filters-bar.tsx'),
    dependencies: [
      'button',
      'date-range-picker',
      'dropdown-menu',
      'search-input',
      'use-page-scroll-threshold'
    ]
  },
  'search-input': {
    relPath: 'components/shared/search-input.tsx',
    content: () => readTemplate('components/shared/search-input.tsx')
  },
  'sort-order-dialog': {
    relPath: 'components/shared/sort-order-dialog.tsx',
    content: () => readTemplate('components/shared/sort-order-dialog.tsx'),
    dependencies: ['sort-order-drag-overlay-item', 'sort-order-item', 'sort-order-types']
  },
  'sort-order-drag-overlay-item': {
    relPath: 'components/shared/sort-order-drag-overlay-item.tsx',
    content: () => readTemplate('components/shared/sort-order-drag-overlay-item.tsx')
  },
  'sort-order-item': {
    relPath: 'components/shared/sort-order-item.tsx',
    content: () => readTemplate('components/shared/sort-order-item.tsx'),
    dependencies: ['sort-order-types']
  },
  'sort-order-types': {
    relPath: 'components/shared/sort-order-types.ts',
    content: () => readTemplate('components/shared/sort-order-types.ts')
  },
  'media-preview': {
    relPath: 'components/shared/media/media-preview.tsx',
    content: () => readTemplate('components/shared/media/media-preview.tsx'),
    dependencies: ['use-failed-image-url']
  },
  'media-grid-item': {
    relPath: 'components/shared/media/media-grid-item.tsx',
    content: () => readTemplate('components/shared/media/media-grid-item.tsx'),
    dependencies: ['media-preview']
  },
  'media-grid': {
    relPath: 'components/shared/media/media-grid.tsx',
    content: () => readTemplate('components/shared/media/media-grid.tsx'),
    dependencies: ['media-grid-item']
  },
  'media-grid-pagination': {
    relPath: 'components/shared/media/media-grid-pagination.tsx',
    content: () => readTemplate('components/shared/media/media-grid-pagination.tsx')
  },
  'media-empty-state': {
    relPath: 'components/shared/media/media-empty-state.tsx',
    content: () => readTemplate('components/shared/media/media-empty-state.tsx')
  },
  'edit-media-dialog': {
    relPath: 'components/shared/media/edit-media-dialog.tsx',
    content: () => readTemplate('components/shared/media/edit-media-dialog.tsx'),
    dependencies: ['edit-media-dialog-content']
  },
  'edit-media-dialog-content': {
    relPath: 'components/shared/media/edit-media-dialog-content.tsx',
    content: () => readTemplate('components/shared/media/edit-media-dialog-content.tsx'),
    dependencies: [
      'media-preview',
      'media-delete-drawer',
      'use-failed-image-url',
      'use-copy-to-clipboard'
    ]
  },
  'media-delete-drawer': {
    relPath: 'components/shared/media/media-delete-drawer.tsx',
    content: () => readTemplate('components/shared/media/media-delete-drawer.tsx')
  },
  'media-delete-dialog': {
    relPath: 'components/shared/media/media-delete-dialog.tsx',
    content: () => readTemplate('components/shared/media/media-delete-dialog.tsx')
  },
  'media-gallery-dialog': {
    relPath: 'components/shared/media/media-gallery-dialog.tsx',
    content: () => readTemplate('components/shared/media/media-gallery-dialog.tsx'),
    dependencies: [
      'upload-dropzone',
      'use-upload',
      'use-media',
      'media-grid',
      'media-grid-pagination',
      'media-url-importer'
    ]
  },
  'media-filters-bar': {
    relPath: 'components/shared/media/media-filters-bar.tsx',
    content: () => readTemplate('components/shared/media/media-filters-bar.tsx'),
    dependencies: ['use-page-scroll-threshold']
  },
  'media-url-importer': {
    relPath: 'components/shared/media/media-url-importer.tsx',
    content: () => readTemplate('components/shared/media/media-url-importer.tsx'),
    dependencies: ['media-utils']
  },
  'data-table': {
    relPath: 'components/shared/data-table/data-table.tsx',
    content: () => readTemplate('components/shared/data-table/data-table.tsx'),
    dependencies: ['use-table-utils', 'data-grid', 'data-table-pagination']
  },
  'data-grid': {
    relPath: 'components/shared/data-table/data-grid.tsx',
    content: () => readTemplate('components/shared/data-table/data-grid.tsx'),
    dependencies: [
      'data-grid-header',
      'data-grid-body',
      'data-grid-row',
      'data-grid-head',
      'data-grid-cell'
    ]
  },
  'data-grid-header': {
    relPath: 'components/shared/data-table/data-grid-header.tsx',
    content: () => readTemplate('components/shared/data-table/data-grid-header.tsx')
  },
  'data-grid-body': {
    relPath: 'components/shared/data-table/data-grid-body.tsx',
    content: () => readTemplate('components/shared/data-table/data-grid-body.tsx')
  },
  'data-grid-row': {
    relPath: 'components/shared/data-table/data-grid-row.tsx',
    content: () => readTemplate('components/shared/data-table/data-grid-row.tsx')
  },
  'data-grid-head': {
    relPath: 'components/shared/data-table/data-grid-head.tsx',
    content: () => readTemplate('components/shared/data-table/data-grid-head.tsx')
  },
  'data-grid-cell': {
    relPath: 'components/shared/data-table/data-grid-cell.tsx',
    content: () => readTemplate('components/shared/data-table/data-grid-cell.tsx')
  },
  'data-table-pagination': {
    relPath: 'components/shared/data-table/data-table-pagination.tsx',
    content: () => readTemplate('components/shared/data-table/data-table-pagination.tsx')
  },
  'use-upload': {
    relPath: 'hooks/use-upload.ts',
    content: () => readTemplate('hooks/use-upload.ts')
  },
  'use-content-editor-source-mode': {
    relPath: 'hooks/content-editor/use-content-editor-source-mode.tsx',
    content: () => readTemplate('hooks/content-editor/use-content-editor-source-mode.tsx'),
    dependencies: [
      'media-field',
      'markdown-utils',
      'content-editor-source-media-utils',
      'tiptap-utils'
    ]
  },
  'use-content-editor': {
    relPath: 'hooks/content-editor/use-content-editor.ts',
    content: () => readTemplate('hooks/content-editor/use-content-editor.ts'),
    dependencies: [
      'admin-globals',
      'content-editor-utils',
      'content-editor-rich-extensions',
      'content-editor-editor-view-utils',
      'content-editor-table-bubble-utils',
      'use-content-editor-slash-menu',
      'tiptap',
      'tiptap-utils',
      'content-editor/media-gallery-block',
      'media-field'
    ]
  },
  'use-content-editor-media-insertion': {
    relPath: 'hooks/content-editor/use-content-editor-media-insertion.ts',
    content: () => readTemplate('hooks/content-editor/use-content-editor-media-insertion.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-blockquote': {
    relPath: 'hooks/content-editor/use-blockquote.ts',
    content: () => readTemplate('hooks/content-editor/use-blockquote.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-code-block': {
    relPath: 'hooks/content-editor/use-code-block.ts',
    content: () => readTemplate('hooks/content-editor/use-code-block.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-color-highlight': {
    relPath: 'hooks/content-editor/use-color-highlight.ts',
    content: () => readTemplate('hooks/content-editor/use-color-highlight.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-heading': {
    relPath: 'hooks/content-editor/use-heading.ts',
    content: () => readTemplate('hooks/content-editor/use-heading.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-heading-dropdown-menu': {
    relPath: 'hooks/content-editor/use-heading-dropdown-menu.ts',
    content: () => readTemplate('hooks/content-editor/use-heading-dropdown-menu.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'use-heading']
  },
  'use-link-popover': {
    relPath: 'hooks/content-editor/use-link-popover.ts',
    content: () => readTemplate('hooks/content-editor/use-link-popover.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-list': {
    relPath: 'hooks/content-editor/use-list.ts',
    content: () => readTemplate('hooks/content-editor/use-list.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-list-dropdown-menu': {
    relPath: 'hooks/content-editor/use-list-dropdown-menu.ts',
    content: () => readTemplate('hooks/content-editor/use-list-dropdown-menu.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'use-list', 'tiptap-utils']
  },
  'use-mark': {
    relPath: 'hooks/content-editor/use-mark.ts',
    content: () => readTemplate('hooks/content-editor/use-mark.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-undo-redo': {
    relPath: 'hooks/content-editor/use-undo-redo.ts',
    content: () => readTemplate('hooks/content-editor/use-undo-redo.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-content-editor-table-add-controls': {
    relPath: 'hooks/content-editor/use-content-editor-table-add-controls.ts',
    content: () => readTemplate('hooks/content-editor/use-content-editor-table-add-controls.ts'),
    dependencies: ['content-editor-table-add-controls-utils', 'tiptap-utils']
  },
  'use-table-button': {
    relPath: 'hooks/content-editor/use-table-button.ts',
    content: () => readTemplate('hooks/content-editor/use-table-button.ts'),
    dependencies: ['content-editor-table-input-utils', 'tiptap-utils']
  },
  'use-content-editor-slash-menu': {
    relPath: 'hooks/content-editor/use-content-editor-slash-menu.ts',
    content: () => readTemplate('hooks/content-editor/use-content-editor-slash-menu.ts'),
    dependencies: ['content-editor-slash-commands-utils', 'tiptap-utils']
  },
  'content-editor/use-menu-navigation': {
    relPath: 'hooks/content-editor/use-menu-navigation.ts',
    content: () => readTemplate('hooks/content-editor/use-menu-navigation.ts')
  },
  'content-editor/use-tiptap-editor': {
    relPath: 'hooks/content-editor/use-tiptap-editor.ts',
    content: () => readTemplate('hooks/content-editor/use-tiptap-editor.ts')
  },
  'use-copy-to-clipboard': {
    relPath: 'hooks/use-copy-to-clipboard.ts',
    content: () => readTemplate('hooks/use-copy-to-clipboard.ts')
  },
  'use-dev-mode-integration': {
    relPath: 'hooks/use-dev-mode-integration.ts',
    content: () => readTemplate('hooks/use-dev-mode-integration.ts')
  },
  'use-local-storage': {
    relPath: 'hooks/use-local-storage.ts',
    content: () => readTemplate('hooks/use-local-storage.ts')
  },
  'use-audit-log': {
    relPath: 'hooks/use-audit-log.ts',
    content: () => readTemplate('hooks/use-audit-log.ts'),
    dependencies: ['audit-action']
  },
  'use-users': { relPath: 'hooks/use-users.ts', content: () => readTemplate('hooks/use-users.ts') },
  'use-webhooks': {
    relPath: 'hooks/use-webhooks.ts',
    content: () => readTemplate('hooks/use-webhooks.ts'),
    dependencies: ['webhooks-action', 'form-settings-action']
  },
  'use-failed-image-url': {
    relPath: 'hooks/use-failed-image-url.ts',
    content: () => readTemplate('hooks/use-failed-image-url.ts')
  },
  'use-entity-versions': {
    relPath: 'hooks/use-entity-versions.ts',
    content: () => readTemplate('hooks/use-entity-versions.ts')
  },
  'use-media': { relPath: 'hooks/use-media.ts', content: () => readTemplate('hooks/use-media.ts') },
  'use-media-filters-bar': {
    relPath: 'hooks/use-media-filters-bar.ts',
    content: () => readTemplate('hooks/use-media-filters-bar.ts')
  },
  'use-table-utils': {
    relPath: 'hooks/use-table-utils.ts',
    content: () => readTemplate('hooks/use-table-utils.ts'),
    dependencies: ['table-utils-core']
  },
  'use-page-boundary-blur-visibility': {
    relPath: 'hooks/use-page-boundary-blur-visibility.ts',
    content: () => readTemplate('hooks/use-page-boundary-blur-visibility.ts'),
    dependencies: ['page-boundary-utils']
  },
  'use-page-scroll-threshold': {
    relPath: 'hooks/use-page-scroll-threshold.ts',
    content: () => readTemplate('hooks/use-page-scroll-threshold.ts')
  },
  'types-index': {
    relPath: 'types/index.ts',
    content: () => readTemplate('types/index.ts')
  },
  'auth-types': {
    relPath: 'types/auth.ts',
    content: () => readTemplate('types/auth.ts')
  },
  'navigation-types': {
    relPath: 'types/navigation.ts',
    content: () => readTemplate('types/navigation.ts')
  },
  'table-meta': {
    relPath: 'types/table-meta.ts',
    content: () => readTemplate('types/table-meta.ts')
  },
  'webhooks-types': {
    relPath: 'types/webhooks.ts',
    content: () => readTemplate('types/webhooks.ts')
  },
  'app-admin': {
    relPath: ({ config }) =>
      `utils/app/${resolveAdminNamespace(config.frameworkConfig.next.namespace).camel}.ts`,
    displayPath: 'utils/app/<namespaceCamel>.ts',
    content: ({ cwd, config }) =>
      adminDataTemplate(detectProjectName(cwd), config.frameworkConfig.next.namespace)
  },
  cn: { relPath: 'utils/shared/cn.ts', content: () => readTemplate('utils/shared/cn.ts') },
  'auth-can-access-admin': {
    relPath: 'utils/auth/can-access-admin.ts',
    content: () => readTemplate('utils/auth/can-access-admin.ts')
  },
  'auth-get-admin-post-login-path': {
    relPath: 'utils/auth/get-admin-post-login-path.ts',
    content: () => readTemplate('utils/auth/get-admin-post-login-path.ts')
  },
  'auth-has-required-role': {
    relPath: 'utils/auth/has-required-role.ts',
    content: () => readTemplate('utils/auth/has-required-role.ts')
  },
  'auth-is-user-role': {
    relPath: 'utils/auth/is-user-role.ts',
    content: () => readTemplate('utils/auth/is-user-role.ts')
  },
  'audit-get-changed-field-names': {
    relPath: 'utils/audit/get-changed-field-names.ts',
    content: () => readTemplate('utils/audit/get-changed-field-names.ts')
  },
  'audit-get-touched-field-names': {
    relPath: 'utils/audit/get-touched-field-names.ts',
    content: () => readTemplate('utils/audit/get-touched-field-names.ts')
  },
  'audit-normalize-comparable-value': {
    relPath: 'utils/audit/normalize-comparable-value.ts',
    content: () => readTemplate('utils/audit/normalize-comparable-value.ts')
  },
  'audit-should-track-field': {
    relPath: 'utils/audit/should-track-field.ts',
    content: () => readTemplate('utils/audit/should-track-field.ts')
  },
  'date-build-date-range': {
    relPath: 'utils/date/build-date-range.ts',
    content: () => readTemplate('utils/date/build-date-range.ts')
  },
  'date-date-range-end-time': {
    relPath: 'utils/date/date-range-end-time.ts',
    content: () => readTemplate('utils/date/date-range-end-time.ts')
  },
  'date-date-range-input-format': {
    relPath: 'utils/date/date-range-input-format.ts',
    content: () => readTemplate('utils/date/date-range-input-format.ts')
  },
  'date-date-range-start-time': {
    relPath: 'utils/date/date-range-start-time.ts',
    content: () => readTemplate('utils/date/date-range-start-time.ts')
  },
  'date-format-date-range-input-date': {
    relPath: 'utils/date/format-date-range-input-date.ts',
    content: () => readTemplate('utils/date/format-date-range-input-date.ts')
  },
  'date-format-date-range-param-date': {
    relPath: 'utils/date/format-date-range-param-date.ts',
    content: () => readTemplate('utils/date/format-date-range-param-date.ts')
  },
  'date-format-date-range-trigger-date': {
    relPath: 'utils/date/format-date-range-trigger-date.ts',
    content: () => readTemplate('utils/date/format-date-range-trigger-date.ts')
  },
  'date-get-date-range-trigger-label': {
    relPath: 'utils/date/get-date-range-trigger-label.ts',
    content: () => readTemplate('utils/date/get-date-range-trigger-label.ts')
  },
  'date-get-exclusive-date-upper-bound': {
    relPath: 'utils/date/get-exclusive-date-upper-bound.ts',
    content: () => readTemplate('utils/date/get-exclusive-date-upper-bound.ts')
  },
  'date-get-ordered-date-range': {
    relPath: 'utils/date/get-ordered-date-range.ts',
    content: () => readTemplate('utils/date/get-ordered-date-range.ts')
  },
  'date-normalize-date-range-time': {
    relPath: 'utils/date/normalize-date-range-time.ts',
    content: () => readTemplate('utils/date/normalize-date-range-time.ts')
  },
  'date-parse-date-input-value': {
    relPath: 'utils/date/parse-date-input-value.ts',
    content: () => readTemplate('utils/date/parse-date-input-value.ts')
  },
  'date-parse-editable-date-range-date': {
    relPath: 'utils/date/parse-editable-date-range-date.ts',
    content: () => readTemplate('utils/date/parse-editable-date-range-date.ts')
  },
  'error-read-string-property': {
    relPath: 'utils/error/read-string-property.ts',
    content: () => readTemplate('utils/error/read-string-property.ts')
  },
  'error-recognize': {
    relPath: 'utils/error/recognize.ts',
    content: () => readTemplate('utils/error/recognize.ts')
  },
  'error-to-action-error': {
    relPath: 'utils/error/to-action-error.ts',
    content: () => readTemplate('utils/error/to-action-error.ts')
  },
  'editor-active-table-cell-class': {
    relPath: 'utils/editor/active-table-cell-class.ts',
    content: () => readTemplate('utils/editor/active-table-cell-class.ts')
  },
  'editor-active-table-cell': {
    relPath: 'utils/editor/active-table-cell.ts',
    content: () => readTemplate('utils/editor/active-table-cell.ts')
  },
  'editor-active-table-wrapper-class': {
    relPath: 'utils/editor/active-table-wrapper-class.ts',
    content: () => readTemplate('utils/editor/active-table-wrapper-class.ts')
  },
  'editor-append-column-to-table': {
    relPath: 'utils/editor/append-column-to-table.ts',
    content: () => readTemplate('utils/editor/append-column-to-table.ts')
  },
  'editor-append-row-to-table': {
    relPath: 'utils/editor/append-row-to-table.ts',
    content: () => readTemplate('utils/editor/append-row-to-table.ts')
  },
  'editor-apply-cursor-change-if-available': {
    relPath: 'utils/editor/apply-cursor-change-if-available.ts',
    content: () => readTemplate('utils/editor/apply-cursor-change-if-available.ts')
  },
  'editor-apply-cursor-change': {
    relPath: 'utils/editor/apply-cursor-change.ts',
    content: () => readTemplate('utils/editor/apply-cursor-change.ts')
  },
  'editor-can-insert-content-editor-media-placeholder': {
    relPath: 'utils/editor/can-insert-content-editor-media-placeholder.ts',
    content: () => readTemplate('utils/editor/can-insert-content-editor-media-placeholder.ts')
  },
  'editor-checkbox-label-factory': {
    relPath: 'utils/editor/checkbox-label-factory.ts',
    content: () => readTemplate('utils/editor/checkbox-label-factory.ts')
  },
  'editor-clear-table-part-preview': {
    relPath: 'utils/editor/clear-table-part-preview.ts',
    content: () => readTemplate('utils/editor/clear-table-part-preview.ts')
  },
  'editor-content-editor-highlight-color-name-pattern': {
    relPath: 'utils/editor/content-editor-highlight-color-name-pattern.ts',
    content: () => readTemplate('utils/editor/content-editor-highlight-color-name-pattern.ts')
  },
  'editor-content-editor-highlight-color-option': {
    relPath: 'utils/editor/content-editor-highlight-color-option.ts',
    content: () => readTemplate('utils/editor/content-editor-highlight-color-option.ts')
  },
  'editor-content-editor-highlight-color-options': {
    relPath: 'utils/editor/content-editor-highlight-color-options.ts',
    content: () => readTemplate('utils/editor/content-editor-highlight-color-options.ts')
  },
  'editor-content-editor-highlight-color': {
    relPath: 'utils/editor/content-editor-highlight-color.ts',
    content: () => readTemplate('utils/editor/content-editor-highlight-color.ts')
  },
  'editor-content-editor-highlight-colors': {
    relPath: 'utils/editor/content-editor-highlight-colors.ts',
    content: () => readTemplate('utils/editor/content-editor-highlight-colors.ts')
  },
  'editor-content-editor-highlight': {
    relPath: 'utils/editor/content-editor-highlight.ts',
    content: () => readTemplate('utils/editor/content-editor-highlight.ts')
  },
  'editor-content-editor-inline-math-delimiter': {
    relPath: 'utils/editor/content-editor-inline-math-delimiter.ts',
    content: () => readTemplate('utils/editor/content-editor-inline-math-delimiter.ts')
  },
  'editor-content-editor-inline-math': {
    relPath: 'utils/editor/content-editor-inline-math.ts',
    content: () => readTemplate('utils/editor/content-editor-inline-math.ts')
  },
  'editor-content-editor-keyboard': {
    relPath: 'utils/editor/content-editor-keyboard.ts',
    content: () => readTemplate('utils/editor/content-editor-keyboard.ts')
  },
  'editor-content-editor-math-kind': {
    relPath: 'utils/editor/content-editor-math-kind.ts',
    content: () => readTemplate('utils/editor/content-editor-math-kind.ts')
  },
  'editor-content-editor-math-value': {
    relPath: 'utils/editor/content-editor-math-value.ts',
    content: () => readTemplate('utils/editor/content-editor-math-value.ts')
  },
  'editor-content-editor-media-placeholder-node': {
    relPath: 'utils/editor/content-editor-media-placeholder-node.ts',
    content: () => readTemplate('utils/editor/content-editor-media-placeholder-node.ts')
  },
  'editor-content-editor-media-shortcut-key': {
    relPath: 'utils/editor/content-editor-media-shortcut-key.ts',
    content: () => readTemplate('utils/editor/content-editor-media-shortcut-key.ts')
  },
  'editor-content-editor-mode': {
    relPath: 'utils/editor/content-editor-mode.ts',
    content: () => readTemplate('utils/editor/content-editor-mode.ts')
  },
  'editor-content-editor-raw-content-block': {
    relPath: 'utils/editor/content-editor-raw-content-block.ts',
    content: () => readTemplate('utils/editor/content-editor-raw-content-block.ts')
  },
  'editor-content-editor-selected-math': {
    relPath: 'utils/editor/content-editor-selected-math.ts',
    content: () => readTemplate('utils/editor/content-editor-selected-math.ts')
  },
  'editor-content-editor-selection-request': {
    relPath: 'utils/editor/content-editor-selection-request.ts',
    content: () => readTemplate('utils/editor/content-editor-selection-request.ts')
  },
  'editor-content-editor-subscript': {
    relPath: 'utils/editor/content-editor-subscript.ts',
    content: () => readTemplate('utils/editor/content-editor-subscript.ts')
  },
  'editor-content-editor-superscript': {
    relPath: 'utils/editor/content-editor-superscript.ts',
    content: () => readTemplate('utils/editor/content-editor-superscript.ts')
  },
  'editor-content-editor-table-alignment': {
    relPath: 'utils/editor/content-editor-table-alignment.ts',
    content: () => readTemplate('utils/editor/content-editor-table-alignment.ts')
  },
  'editor-content-editor-underline': {
    relPath: 'utils/editor/content-editor-underline.ts',
    content: () => readTemplate('utils/editor/content-editor-underline.ts')
  },
  'editor-create-content-editor-table-node': {
    relPath: 'utils/editor/create-content-editor-table-node.ts',
    content: () => readTemplate('utils/editor/create-content-editor-table-node.ts')
  },
  'editor-create-html-mark': {
    relPath: 'utils/editor/create-html-mark.ts',
    content: () => readTemplate('utils/editor/create-html-mark.ts')
  },
  'editor-create-markdown-image-text': {
    relPath: 'utils/editor/create-markdown-image-text.ts',
    content: () => readTemplate('utils/editor/create-markdown-image-text.ts')
  },
  'editor-create-markdown-plain-text-offset-map': {
    relPath: 'utils/editor/create-markdown-plain-text-offset-map.ts',
    content: () => readTemplate('utils/editor/create-markdown-plain-text-offset-map.ts')
  },
  'editor-create-markdown-table-text': {
    relPath: 'utils/editor/create-markdown-table-text.ts',
    content: () => readTemplate('utils/editor/create-markdown-table-text.ts')
  },
  'editor-create-markdown-text-from-url': {
    relPath: 'utils/editor/create-markdown-text-from-url.ts',
    content: () => readTemplate('utils/editor/create-markdown-text-from-url.ts')
  },
  'editor-default-content-editor-table-cols': {
    relPath: 'utils/editor/default-content-editor-table-cols.ts',
    content: () => readTemplate('utils/editor/default-content-editor-table-cols.ts')
  },
  'editor-default-content-editor-table-rows': {
    relPath: 'utils/editor/default-content-editor-table-rows.ts',
    content: () => readTemplate('utils/editor/default-content-editor-table-rows.ts')
  },
  'editor-doc-position-from-plain-text-offset': {
    relPath: 'utils/editor/doc-position-from-plain-text-offset.ts',
    content: () => readTemplate('utils/editor/doc-position-from-plain-text-offset.ts')
  },
  'editor-escape-html': {
    relPath: 'utils/editor/escape-html.ts',
    content: () => readTemplate('utils/editor/escape-html.ts')
  },
  'editor-find-highlight-span-range': {
    relPath: 'utils/editor/find-highlight-span-range.ts',
    content: () => readTemplate('utils/editor/find-highlight-span-range.ts')
  },
  'editor-find-inline-math-start': {
    relPath: 'utils/editor/find-inline-math-start.ts',
    content: () => readTemplate('utils/editor/find-inline-math-start.ts')
  },
  'editor-find-markdown-link-range': {
    relPath: 'utils/editor/find-markdown-link-range.ts',
    content: () => readTemplate('utils/editor/find-markdown-link-range.ts')
  },
  'editor-find-node-at-position': {
    relPath: 'utils/editor/find-node-at-position.ts',
    content: () => readTemplate('utils/editor/find-node-at-position.ts')
  },
  'editor-find-node-position': {
    relPath: 'utils/editor/find-node-position.ts',
    content: () => readTemplate('utils/editor/find-node-position.ts')
  },
  'editor-find-raw-content-start': {
    relPath: 'utils/editor/find-raw-content-start.ts',
    content: () => readTemplate('utils/editor/find-raw-content-start.ts')
  },
  'editor-find-unescaped-character': {
    relPath: 'utils/editor/find-unescaped-character.ts',
    content: () => readTemplate('utils/editor/find-unescaped-character.ts')
  },
  'editor-find-unescaped-sequence': {
    relPath: 'utils/editor/find-unescaped-sequence.ts',
    content: () => readTemplate('utils/editor/find-unescaped-sequence.ts')
  },
  'editor-focus-next-node': {
    relPath: 'utils/editor/focus-next-node.ts',
    content: () => readTemplate('utils/editor/focus-next-node.ts')
  },
  'editor-format-markdown-blockquote-line': {
    relPath: 'utils/editor/format-markdown-blockquote-line.ts',
    content: () => readTemplate('utils/editor/format-markdown-blockquote-line.ts')
  },
  'editor-format-markdown-heading-line': {
    relPath: 'utils/editor/format-markdown-heading-line.ts',
    content: () => readTemplate('utils/editor/format-markdown-heading-line.ts')
  },
  'editor-format-markdown-list-line': {
    relPath: 'utils/editor/format-markdown-list-line.ts',
    content: () => readTemplate('utils/editor/format-markdown-list-line.ts')
  },
  'editor-format-markdown': {
    relPath: 'utils/editor/format-markdown.ts',
    content: () => readTemplate('utils/editor/format-markdown.ts')
  },
  'editor-format-shortcut-key': {
    relPath: 'utils/editor/format-shortcut-key.ts',
    content: () => readTemplate('utils/editor/format-shortcut-key.ts')
  },
  'editor-get-active-table-column-alignment': {
    relPath: 'utils/editor/get-active-table-column-alignment.ts',
    content: () => readTemplate('utils/editor/get-active-table-column-alignment.ts')
  },
  'editor-get-block-math-expression': {
    relPath: 'utils/editor/get-block-math-expression.ts',
    content: () => readTemplate('utils/editor/get-block-math-expression.ts')
  },
  'editor-get-closest-table-cell': {
    relPath: 'utils/editor/get-closest-table-cell.ts',
    content: () => readTemplate('utils/editor/get-closest-table-cell.ts')
  },
  'editor-get-content-editor-highlight-color-name': {
    relPath: 'utils/editor/get-content-editor-highlight-color-name.ts',
    content: () => readTemplate('utils/editor/get-content-editor-highlight-color-name.ts')
  },
  'editor-get-content-editor-highlight-color-value': {
    relPath: 'utils/editor/get-content-editor-highlight-color-value.ts',
    content: () => readTemplate('utils/editor/get-content-editor-highlight-color-value.ts')
  },
  'editor-get-editor-element': {
    relPath: 'utils/editor/get-editor-element.ts',
    content: () => readTemplate('utils/editor/get-editor-element.ts')
  },
  'editor-get-editor-scroll-element': {
    relPath: 'utils/editor/get-editor-scroll-element.ts',
    content: () => readTemplate('utils/editor/get-editor-scroll-element.ts')
  },
  'editor-get-event-table-cell': {
    relPath: 'utils/editor/get-event-table-cell.ts',
    content: () => readTemplate('utils/editor/get-event-table-cell.ts')
  },
  'editor-get-inline-math-delimiter': {
    relPath: 'utils/editor/get-inline-math-delimiter.ts',
    content: () => readTemplate('utils/editor/get-inline-math-delimiter.ts')
  },
  'editor-get-markdown-heading-fallback': {
    relPath: 'utils/editor/get-markdown-heading-fallback.ts',
    content: () => readTemplate('utils/editor/get-markdown-heading-fallback.ts')
  },
  'editor-get-markdown-list-fallback': {
    relPath: 'utils/editor/get-markdown-list-fallback.ts',
    content: () => readTemplate('utils/editor/get-markdown-list-fallback.ts')
  },
  'editor-get-media-label': {
    relPath: 'utils/editor/get-media-label.ts',
    content: () => readTemplate('utils/editor/get-media-label.ts')
  },
  'editor-get-number-attr': {
    relPath: 'utils/editor/get-number-attr.ts',
    content: () => readTemplate('utils/editor/get-number-attr.ts')
  },
  'editor-get-plain-text-offset-before-doc-position': {
    relPath: 'utils/editor/get-plain-text-offset-before-doc-position.ts',
    content: () => readTemplate('utils/editor/get-plain-text-offset-before-doc-position.ts')
  },
  'editor-get-plain-text-offset-before-markdown-offset': {
    relPath: 'utils/editor/get-plain-text-offset-before-markdown-offset.ts',
    content: () => readTemplate('utils/editor/get-plain-text-offset-before-markdown-offset.ts')
  },
  'editor-get-raw-content-kind': {
    relPath: 'utils/editor/get-raw-content-kind.ts',
    content: () => readTemplate('utils/editor/get-raw-content-kind.ts')
  },
  'editor-get-raw-content-token': {
    relPath: 'utils/editor/get-raw-content-token.ts',
    content: () => readTemplate('utils/editor/get-raw-content-token.ts')
  },
  'editor-get-selected-block-nodes': {
    relPath: 'utils/editor/get-selected-block-nodes.ts',
    content: () => readTemplate('utils/editor/get-selected-block-nodes.ts')
  },
  'editor-get-selected-content-editor-math': {
    relPath: 'utils/editor/get-selected-content-editor-math.ts',
    content: () => readTemplate('utils/editor/get-selected-content-editor-math.ts')
  },
  'editor-get-selected-nodes-of-type': {
    relPath: 'utils/editor/get-selected-nodes-of-type.ts',
    content: () => readTemplate('utils/editor/get-selected-nodes-of-type.ts')
  },
  'editor-get-selected-table-column-cell-positions': {
    relPath: 'utils/editor/get-selected-table-column-cell-positions.ts',
    content: () => readTemplate('utils/editor/get-selected-table-column-cell-positions.ts')
  },
  'editor-get-selection-table-cell': {
    relPath: 'utils/editor/get-selection-table-cell.ts',
    content: () => readTemplate('utils/editor/get-selection-table-cell.ts')
  },
  'editor-get-selection-table-element': {
    relPath: 'utils/editor/get-selection-table-element.ts',
    content: () => readTemplate('utils/editor/get-selection-table-element.ts')
  },
  'editor-get-selection-table': {
    relPath: 'utils/editor/get-selection-table.ts',
    content: () => readTemplate('utils/editor/get-selection-table.ts')
  },
  'editor-get-slash-menu-state': {
    relPath: 'utils/editor/get-slash-menu-state.ts',
    content: () => readTemplate('utils/editor/get-slash-menu-state.ts')
  },
  'editor-get-string-attr': {
    relPath: 'utils/editor/get-string-attr.ts',
    content: () => readTemplate('utils/editor/get-string-attr.ts')
  },
  'editor-get-table-element': {
    relPath: 'utils/editor/get-table-element.ts',
    content: () => readTemplate('utils/editor/get-table-element.ts')
  },
  'editor-get-table-from-event-target': {
    relPath: 'utils/editor/get-table-from-event-target.ts',
    content: () => readTemplate('utils/editor/get-table-from-event-target.ts')
  },
  'editor-get-table-from-wrapper': {
    relPath: 'utils/editor/get-table-from-wrapper.ts',
    content: () => readTemplate('utils/editor/get-table-from-wrapper.ts')
  },
  'editor-get-table-position-for-element': {
    relPath: 'utils/editor/get-table-position-for-element.ts',
    content: () => readTemplate('utils/editor/get-table-position-for-element.ts')
  },
  'editor-get-table-rect-at-position': {
    relPath: 'utils/editor/get-table-rect-at-position.ts',
    content: () => readTemplate('utils/editor/get-table-rect-at-position.ts')
  },
  'editor-get-table-wrapper-element': {
    relPath: 'utils/editor/get-table-wrapper-element.ts',
    content: () => readTemplate('utils/editor/get-table-wrapper-element.ts')
  },
  'editor-get-task-item-checkbox-label': {
    relPath: 'utils/editor/get-task-item-checkbox-label.ts',
    content: () => readTemplate('utils/editor/get-task-item-checkbox-label.ts')
  },
  'editor-has-own-attribute': {
    relPath: 'utils/editor/has-own-attribute.ts',
    content: () => readTemplate('utils/editor/has-own-attribute.ts')
  },
  'editor-highlight-span-pattern': {
    relPath: 'utils/editor/highlight-span-pattern.ts',
    content: () => readTemplate('utils/editor/highlight-span-pattern.ts')
  },
  'editor-inline-math-delimiter': {
    relPath: 'utils/editor/inline-math-delimiter.ts',
    content: () => readTemplate('utils/editor/inline-math-delimiter.ts')
  },
  'editor-inline-math-node-view': {
    relPath: 'utils/editor/inline-math-node-view.ts',
    content: () => readTemplate('utils/editor/inline-math-node-view.ts')
  },
  'editor-insert-component-at-cursor': {
    relPath: 'utils/editor/insert-component-at-cursor.ts',
    content: () => readTemplate('utils/editor/insert-component-at-cursor.ts')
  },
  'editor-insert-content-editor-math': {
    relPath: 'utils/editor/insert-content-editor-math.ts',
    content: () => readTemplate('utils/editor/insert-content-editor-math.ts')
  },
  'editor-insert-content-editor-media-placeholder': {
    relPath: 'utils/editor/insert-content-editor-media-placeholder.ts',
    content: () => readTemplate('utils/editor/insert-content-editor-media-placeholder.ts')
  },
  'editor-insert-content-editor-table': {
    relPath: 'utils/editor/insert-content-editor-table.ts',
    content: () => readTemplate('utils/editor/insert-content-editor-table.ts')
  },
  'editor-insert-markdown-text-at-position': {
    relPath: 'utils/editor/insert-markdown-text-at-position.ts',
    content: () => readTemplate('utils/editor/insert-markdown-text-at-position.ts')
  },
  'editor-insert-result': {
    relPath: 'utils/editor/insert-result.ts',
    content: () => readTemplate('utils/editor/insert-result.ts')
  },
  'editor-insert-text-at-cursor': {
    relPath: 'utils/editor/insert-text-at-cursor.ts',
    content: () => readTemplate('utils/editor/insert-text-at-cursor.ts')
  },
  'editor-is-allowed-uri': {
    relPath: 'utils/editor/is-allowed-uri.ts',
    content: () => readTemplate('utils/editor/is-allowed-uri.ts')
  },
  'editor-is-content-editor-highlight-color': {
    relPath: 'utils/editor/is-content-editor-highlight-color.ts',
    content: () => readTemplate('utils/editor/is-content-editor-highlight-color.ts')
  },
  'editor-is-content-editor-media-placeholder-active': {
    relPath: 'utils/editor/is-content-editor-media-placeholder-active.ts',
    content: () => readTemplate('utils/editor/is-content-editor-media-placeholder-active.ts')
  },
  'editor-is-content-editor-table-alignment': {
    relPath: 'utils/editor/is-content-editor-table-alignment.ts',
    content: () => readTemplate('utils/editor/is-content-editor-table-alignment.ts')
  },
  'editor-is-element-visible-in-editor': {
    relPath: 'utils/editor/is-element-visible-in-editor.ts',
    content: () => readTemplate('utils/editor/is-element-visible-in-editor.ts')
  },
  'editor-is-escaped': {
    relPath: 'utils/editor/is-escaped.ts',
    content: () => readTemplate('utils/editor/is-escaped.ts')
  },
  'editor-is-extension-available': {
    relPath: 'utils/editor/is-extension-available.ts',
    content: () => readTemplate('utils/editor/is-extension-available.ts')
  },
  'editor-is-mac': {
    relPath: 'utils/editor/is-mac.ts',
    content: () => readTemplate('utils/editor/is-mac.ts')
  },
  'editor-is-mark-in-schema': {
    relPath: 'utils/editor/is-mark-in-schema.ts',
    content: () => readTemplate('utils/editor/is-mark-in-schema.ts')
  },
  'editor-is-markdown-line-start': {
    relPath: 'utils/editor/is-markdown-line-start.ts',
    content: () => readTemplate('utils/editor/is-markdown-line-start.ts')
  },
  'editor-is-node-in-schema': {
    relPath: 'utils/editor/is-node-in-schema.ts',
    content: () => readTemplate('utils/editor/is-node-in-schema.ts')
  },
  'editor-is-node-type-selected': {
    relPath: 'utils/editor/is-node-type-selected.ts',
    content: () => readTemplate('utils/editor/is-node-type-selected.ts')
  },
  'editor-is-same-slash-menu-state': {
    relPath: 'utils/editor/is-same-slash-menu-state.ts',
    content: () => readTemplate('utils/editor/is-same-slash-menu-state.ts')
  },
  'editor-is-selected-table-visible': {
    relPath: 'utils/editor/is-selected-table-visible.ts',
    content: () => readTemplate('utils/editor/is-selected-table-visible.ts')
  },
  'editor-is-task-item-checked': {
    relPath: 'utils/editor/is-task-item-checked.ts',
    content: () => readTemplate('utils/editor/is-task-item-checked.ts')
  },
  'editor-is-valid-dollar-inline-math': {
    relPath: 'utils/editor/is-valid-dollar-inline-math.ts',
    content: () => readTemplate('utils/editor/is-valid-dollar-inline-math.ts')
  },
  'editor-is-valid-position': {
    relPath: 'utils/editor/is-valid-position.ts',
    content: () => readTemplate('utils/editor/is-valid-position.ts')
  },
  'editor-is-word-character': {
    relPath: 'utils/editor/is-word-character.ts',
    content: () => readTemplate('utils/editor/is-word-character.ts')
  },
  'editor-mac-symbols': {
    relPath: 'utils/editor/mac-symbols.ts',
    content: () => readTemplate('utils/editor/mac-symbols.ts')
  },
  'editor-markdown-heading-level': {
    relPath: 'utils/editor/markdown-heading-level.ts',
    content: () => readTemplate('utils/editor/markdown-heading-level.ts')
  },
  'editor-markdown-heading-option': {
    relPath: 'utils/editor/markdown-heading-option.ts',
    content: () => readTemplate('utils/editor/markdown-heading-option.ts')
  },
  'editor-markdown-heading-options': {
    relPath: 'utils/editor/markdown-heading-options.ts',
    content: () => readTemplate('utils/editor/markdown-heading-options.ts')
  },
  'editor-markdown-image-upload-result': {
    relPath: 'utils/editor/markdown-image-upload-result.ts',
    content: () => readTemplate('utils/editor/markdown-image-upload-result.ts')
  },
  'editor-markdown-list-option': {
    relPath: 'utils/editor/markdown-list-option.ts',
    content: () => readTemplate('utils/editor/markdown-list-option.ts')
  },
  'editor-markdown-list-options': {
    relPath: 'utils/editor/markdown-list-options.ts',
    content: () => readTemplate('utils/editor/markdown-list-options.ts')
  },
  'editor-markdown-list-type': {
    relPath: 'utils/editor/markdown-list-type.ts',
    content: () => readTemplate('utils/editor/markdown-list-type.ts')
  },
  'editor-markdown-offset-from-plain-text-offset': {
    relPath: 'utils/editor/markdown-offset-from-plain-text-offset.ts',
    content: () => readTemplate('utils/editor/markdown-offset-from-plain-text-offset.ts')
  },
  'editor-match-block-math': {
    relPath: 'utils/editor/match-block-math.ts',
    content: () => readTemplate('utils/editor/match-block-math.ts')
  },
  'editor-match-code-fence-start': {
    relPath: 'utils/editor/match-code-fence-start.ts',
    content: () => readTemplate('utils/editor/match-code-fence-start.ts')
  },
  'editor-match-html-block': {
    relPath: 'utils/editor/match-html-block.ts',
    content: () => readTemplate('utils/editor/match-html-block.ts')
  },
  'editor-match-html-mark-syntax': {
    relPath: 'utils/editor/match-html-mark-syntax.ts',
    content: () => readTemplate('utils/editor/match-html-mark-syntax.ts')
  },
  'editor-match-inline-math': {
    relPath: 'utils/editor/match-inline-math.ts',
    content: () => readTemplate('utils/editor/match-inline-math.ts')
  },
  'editor-match-markdown-block-prefix': {
    relPath: 'utils/editor/match-markdown-block-prefix.ts',
    content: () => readTemplate('utils/editor/match-markdown-block-prefix.ts')
  },
  'editor-match-markdown-mark-syntax': {
    relPath: 'utils/editor/match-markdown-mark-syntax.ts',
    content: () => readTemplate('utils/editor/match-markdown-mark-syntax.ts')
  },
  'editor-match-raw-content-block': {
    relPath: 'utils/editor/match-raw-content-block.ts',
    content: () => readTemplate('utils/editor/match-raw-content-block.ts')
  },
  'editor-mounted-editor-view': {
    relPath: 'utils/editor/mounted-editor-view.ts',
    content: () => readTemplate('utils/editor/mounted-editor-view.ts')
  },
  'editor-normalize-editor-markdown': {
    relPath: 'utils/editor/normalize-editor-markdown.ts',
    content: () => readTemplate('utils/editor/normalize-editor-markdown.ts')
  },
  'editor-parse-content-editor-math-input': {
    relPath: 'utils/editor/parse-content-editor-math-input.ts',
    content: () => readTemplate('utils/editor/parse-content-editor-math-input.ts')
  },
  'editor-parse-shortcut-keys': {
    relPath: 'utils/editor/parse-shortcut-keys.ts',
    content: () => readTemplate('utils/editor/parse-shortcut-keys.ts')
  },
  'editor-prepare-markdown-for-rich-mode': {
    relPath: 'utils/editor/prepare-markdown-for-rich-mode.ts',
    content: () => readTemplate('utils/editor/prepare-markdown-for-rich-mode.ts')
  },
  'editor-protocol-config': {
    relPath: 'utils/editor/protocol-config.ts',
    content: () => readTemplate('utils/editor/protocol-config.ts')
  },
  'editor-raw-content-block-node-view': {
    relPath: 'utils/editor/raw-content-block-node-view.ts',
    content: () => readTemplate('utils/editor/raw-content-block-node-view.ts')
  },
  'editor-raw-content-kind': {
    relPath: 'utils/editor/raw-content-kind.ts',
    content: () => readTemplate('utils/editor/raw-content-kind.ts')
  },
  'editor-raw-content-match': {
    relPath: 'utils/editor/raw-content-match.ts',
    content: () => readTemplate('utils/editor/raw-content-match.ts')
  },
  'editor-raw-html-block-tags': {
    relPath: 'utils/editor/raw-html-block-tags.ts',
    content: () => readTemplate('utils/editor/raw-html-block-tags.ts')
  },
  'editor-remove-highlight-span-at-cursor': {
    relPath: 'utils/editor/remove-highlight-span-at-cursor.ts',
    content: () => readTemplate('utils/editor/remove-highlight-span-at-cursor.ts')
  },
  'editor-remove-markdown-link-at-cursor': {
    relPath: 'utils/editor/remove-markdown-link-at-cursor.ts',
    content: () => readTemplate('utils/editor/remove-markdown-link-at-cursor.ts')
  },
  'editor-remove-selected-content-editor-math': {
    relPath: 'utils/editor/remove-selected-content-editor-math.ts',
    content: () => readTemplate('utils/editor/remove-selected-content-editor-math.ts')
  },
  'editor-render-details-preview': {
    relPath: 'utils/editor/render-details-preview.ts',
    content: () => readTemplate('utils/editor/render-details-preview.ts')
  },
  'editor-render-latex': {
    relPath: 'utils/editor/render-latex.ts',
    content: () => readTemplate('utils/editor/render-latex.ts')
  },
  'editor-render-raw-content-preview': {
    relPath: 'utils/editor/render-raw-content-preview.ts',
    content: () => readTemplate('utils/editor/render-raw-content-preview.ts')
  },
  'editor-render-simple-markdown-body': {
    relPath: 'utils/editor/render-simple-markdown-body.ts',
    content: () => readTemplate('utils/editor/render-simple-markdown-body.ts')
  },
  'editor-run-slash-command': {
    relPath: 'utils/editor/run-slash-command.ts',
    content: () => readTemplate('utils/editor/run-slash-command.ts')
  },
  'editor-runnable-slash-command': {
    relPath: 'utils/editor/runnable-slash-command.ts',
    content: () => readTemplate('utils/editor/runnable-slash-command.ts')
  },
  'editor-sanitize-html-preview': {
    relPath: 'utils/editor/sanitize-html-preview.ts',
    content: () => readTemplate('utils/editor/sanitize-html-preview.ts')
  },
  'editor-sanitize-url': {
    relPath: 'utils/editor/sanitize-url.ts',
    content: () => readTemplate('utils/editor/sanitize-url.ts')
  },
  'editor-select-current-block-content': {
    relPath: 'utils/editor/select-current-block-content.ts',
    content: () => readTemplate('utils/editor/select-current-block-content.ts')
  },
  'editor-selection-within-convertible-types': {
    relPath: 'utils/editor/selection-within-convertible-types.ts',
    content: () => readTemplate('utils/editor/selection-within-convertible-types.ts')
  },
  'editor-self-closing-html-block-tags': {
    relPath: 'utils/editor/self-closing-html-block-tags.ts',
    content: () => readTemplate('utils/editor/self-closing-html-block-tags.ts')
  },
  'editor-serialize-raw-content-preview': {
    relPath: 'utils/editor/serialize-raw-content-preview.ts',
    content: () => readTemplate('utils/editor/serialize-raw-content-preview.ts')
  },
  'editor-set-active-table-column-alignment': {
    relPath: 'utils/editor/set-active-table-column-alignment.ts',
    content: () => readTemplate('utils/editor/set-active-table-column-alignment.ts')
  },
  'editor-set-element-attribute': {
    relPath: 'utils/editor/set-element-attribute.ts',
    content: () => readTemplate('utils/editor/set-element-attribute.ts')
  },
  'editor-set-markdown-link-at-cursor': {
    relPath: 'utils/editor/set-markdown-link-at-cursor.ts',
    content: () => readTemplate('utils/editor/set-markdown-link-at-cursor.ts')
  },
  'editor-show-table-part-preview': {
    relPath: 'utils/editor/show-table-part-preview.ts',
    content: () => readTemplate('utils/editor/show-table-part-preview.ts')
  },
  'editor-slash-menu-state': {
    relPath: 'utils/editor/slash-menu-state.ts',
    content: () => readTemplate('utils/editor/slash-menu-state.ts')
  },
  'editor-sr-only': {
    relPath: 'utils/editor/sr-only.ts',
    content: () => readTemplate('utils/editor/sr-only.ts')
  },
  'editor-strip-html': {
    relPath: 'utils/editor/strip-html.ts',
    content: () => readTemplate('utils/editor/strip-html.ts')
  },
  'editor-strip-markdown-block-prefix': {
    relPath: 'utils/editor/strip-markdown-block-prefix.ts',
    content: () => readTemplate('utils/editor/strip-markdown-block-prefix.ts')
  },
  'editor-table-add-controls-state': {
    relPath: 'utils/editor/table-add-controls-state.ts',
    content: () => readTemplate('utils/editor/table-add-controls-state.ts')
  },
  'editor-table-part-preview-class': {
    relPath: 'utils/editor/table-part-preview-class.ts',
    content: () => readTemplate('utils/editor/table-part-preview-class.ts')
  },
  'editor-table-part': {
    relPath: 'utils/editor/table-part.ts',
    content: () => readTemplate('utils/editor/table-part.ts')
  },
  'editor-table-wrapper-class': {
    relPath: 'utils/editor/table-wrapper-class.ts',
    content: () => readTemplate('utils/editor/table-wrapper-class.ts')
  },
  'editor-transform-selected-markdown-lines': {
    relPath: 'utils/editor/transform-selected-markdown-lines.ts',
    content: () => readTemplate('utils/editor/transform-selected-markdown-lines.ts')
  },
  'editor-update-nodes-attr': {
    relPath: 'utils/editor/update-nodes-attr.ts',
    content: () => readTemplate('utils/editor/update-nodes-attr.ts')
  },
  'editor-update-selected-content-editor-math': {
    relPath: 'utils/editor/update-selected-content-editor-math.ts',
    content: () => readTemplate('utils/editor/update-selected-content-editor-math.ts')
  },
  'content-editor-editor-view-utils': {
    relPath: 'utils/editor/editor-view.ts',
    content: () => readTemplate('utils/editor/editor-view.ts')
  },
  'content-editor-slash-commands-utils': {
    relPath: 'utils/editor/slash-commands.ts',
    content: () => readTemplate('utils/editor/slash-commands.ts')
  },
  'content-editor-table-input-utils': {
    relPath: 'utils/editor/table-input.ts',
    content: () => readTemplate('utils/editor/table-input.ts')
  },
  'email-escape-html': {
    relPath: 'utils/email/escape-html.ts',
    content: () => readTemplate('utils/email/escape-html.ts')
  },
  'email-form-email-delivery-input': {
    relPath: 'utils/email/form-email-delivery-input.ts',
    content: () => readTemplate('utils/email/form-email-delivery-input.ts')
  },
  'email-format-email-value': {
    relPath: 'utils/email/format-email-value.ts',
    content: () => readTemplate('utils/email/format-email-value.ts')
  },
  'email-is-valid-email': {
    relPath: 'utils/email/is-valid-email.ts',
    content: () => readTemplate('utils/email/is-valid-email.ts')
  },
  'email-normalize-email-list': {
    relPath: 'utils/email/normalize-email-list.ts',
    content: () => readTemplate('utils/email/normalize-email-list.ts')
  },
  'email-render-form-submission-rows': {
    relPath: 'utils/email/render-form-submission-rows.ts',
    content: () => readTemplate('utils/email/render-form-submission-rows.ts')
  },
  'email-render-notification-email': {
    relPath: 'utils/email/render-notification-email.ts',
    content: () => readTemplate('utils/email/render-notification-email.ts')
  },
  'email-render-thank-you-email': {
    relPath: 'utils/email/render-thank-you-email.ts',
    content: () => readTemplate('utils/email/render-thank-you-email.ts')
  },
  'form-clone-default-value': {
    relPath: 'utils/form/clone-default-value.ts',
    content: () => readTemplate('utils/form/clone-default-value.ts')
  },
  'form-create-list-item-key': {
    relPath: 'utils/form/create-list-item-key.ts',
    content: () => readTemplate('utils/form/create-list-item-key.ts')
  },
  'media-admin-media-type': {
    relPath: 'utils/media/admin-media-type.ts',
    content: () => readTemplate('utils/media/admin-media-type.ts')
  },
  'media-create-admin-media-from-url': {
    relPath: 'utils/media/create-admin-media-from-url.ts',
    content: () => readTemplate('utils/media/create-admin-media-from-url.ts')
  },
  'media-create-preview-url': {
    relPath: 'utils/media/create-preview-url.ts',
    content: () => readTemplate('utils/media/create-preview-url.ts')
  },
  'media-get-filename-from-url': {
    relPath: 'utils/media/get-filename-from-url.ts',
    content: () => readTemplate('utils/media/get-filename-from-url.ts')
  },
  'media-get-media-type': {
    relPath: 'utils/media/get-media-type.ts',
    content: () => readTemplate('utils/media/get-media-type.ts')
  },
  'media-get-media-url-help-text': {
    relPath: 'utils/media/get-media-url-help-text.ts',
    content: () => readTemplate('utils/media/get-media-url-help-text.ts')
  },
  'media-get-media-url-placeholder': {
    relPath: 'utils/media/get-media-url-placeholder.ts',
    content: () => readTemplate('utils/media/get-media-url-placeholder.ts')
  },
  'media-image-extensions': {
    relPath: 'utils/media/image-extensions.ts',
    content: () => readTemplate('utils/media/image-extensions.ts')
  },
  'media-image-mime-types': {
    relPath: 'utils/media/image-mime-types.ts',
    content: () => readTemplate('utils/media/image-mime-types.ts')
  },
  'media-infer-media-content-type': {
    relPath: 'utils/media/infer-media-content-type.ts',
    content: () => readTemplate('utils/media/infer-media-content-type.ts')
  },
  'media-is-document-only-accept': {
    relPath: 'utils/media/is-document-only-accept.ts',
    content: () => readTemplate('utils/media/is-document-only-accept.ts')
  },
  'media-map-media-row': {
    relPath: 'utils/media/map-media-row.ts',
    content: () => readTemplate('utils/media/map-media-row.ts')
  },
  'media-media-mime-types': {
    relPath: 'utils/media/media-mime-types.ts',
    content: () => readTemplate('utils/media/media-mime-types.ts')
  },
  'media-revoke-preview-url': {
    relPath: 'utils/media/revoke-preview-url.ts',
    content: () => readTemplate('utils/media/revoke-preview-url.ts')
  },
  'media-select-media': {
    relPath: 'utils/media/select-media.ts',
    content: () => readTemplate('utils/media/select-media.ts')
  },
  'media-video-extensions': {
    relPath: 'utils/media/video-extensions.ts',
    content: () => readTemplate('utils/media/video-extensions.ts')
  },
  'media-video-mime-types': {
    relPath: 'utils/media/video-mime-types.ts',
    content: () => readTemplate('utils/media/video-mime-types.ts')
  },
  'navigation-order-utils': {
    relPath: 'utils/navigation/order.ts',
    content: () => readTemplate('utils/navigation/order.ts')
  },
  'seo-create-metadata': {
    relPath: 'utils/seo/create-metadata.ts',
    content: () => readTemplate('utils/seo/create-metadata.ts')
  },
  'seo-generate-article-schema': {
    relPath: 'utils/seo/generate-article-schema.ts',
    content: () => readTemplate('utils/seo/generate-article-schema.ts')
  },
  'seo-schema-to-json': {
    relPath: 'utils/seo/schema-to-json.ts',
    content: () => readTemplate('utils/seo/schema-to-json.ts')
  },
  'sidebar-utils': {
    relPath: 'utils/navigation/sidebar.ts',
    content: () => readTemplate('utils/navigation/sidebar.ts')
  },
  'page-boundary-utils': {
    relPath: 'utils/page/boundary.ts',
    content: () => readTemplate('utils/page/boundary.ts')
  },
  'storage-build-local-storage-key': {
    relPath: 'utils/storage/build-local-storage-key.ts',
    content: () => readTemplate('utils/storage/build-local-storage-key.ts')
  },
  'storage-sanitize-filename': {
    relPath: 'utils/storage/sanitize-filename.ts',
    content: () => readTemplate('utils/storage/sanitize-filename.ts')
  },
  'table-default-get-id': {
    relPath: 'utils/table/default-get-id.ts',
    content: () => readTemplate('utils/table/default-get-id.ts')
  },
  'table-is-string': {
    relPath: 'utils/table/is-string.ts',
    content: () => readTemplate('utils/table/is-string.ts')
  },
  'text-utils': {
    relPath: 'utils/text/text.ts',
    content: () => readTemplate('utils/text/text.ts')
  },
  'upload-default-remote-accept': {
    relPath: 'utils/upload/default-remote-accept.ts',
    content: () => readTemplate('utils/upload/default-remote-accept.ts')
  },
  'upload-dropzone-accept': {
    relPath: 'utils/upload/dropzone-accept.ts',
    content: () => readTemplate('utils/upload/dropzone-accept.ts')
  },
  'upload-get-fallback-extension': {
    relPath: 'utils/upload/get-fallback-extension.ts',
    content: () => readTemplate('utils/upload/get-fallback-extension.ts')
  },
  'upload-get-remote-filename': {
    relPath: 'utils/upload/get-remote-filename.ts',
    content: () => readTemplate('utils/upload/get-remote-filename.ts')
  },
  'upload-is-accepted-remote-type': {
    relPath: 'utils/upload/is-accepted-remote-type.ts',
    content: () => readTemplate('utils/upload/is-accepted-remote-type.ts')
  },
  'upload-is-wildcard-accept': {
    relPath: 'utils/upload/is-wildcard-accept.ts',
    content: () => readTemplate('utils/upload/is-wildcard-accept.ts')
  },
  'upload-parse-accept-types': {
    relPath: 'utils/upload/parse-accept-types.ts',
    content: () => readTemplate('utils/upload/parse-accept-types.ts')
  },
  'upload-parse-dropzone-accept': {
    relPath: 'utils/upload/parse-dropzone-accept.ts',
    content: () => readTemplate('utils/upload/parse-dropzone-accept.ts')
  },
  'upload-remote-fetch-timeout-ms': {
    relPath: 'utils/upload/remote-fetch-timeout-ms.ts',
    content: () => readTemplate('utils/upload/remote-fetch-timeout-ms.ts')
  },
  'user-compare-user-sort-values': {
    relPath: 'utils/user/compare-user-sort-values.ts',
    content: () => readTemplate('utils/user/compare-user-sort-values.ts')
  },
  'user-get-user-initials': {
    relPath: 'utils/user/get-user-initials.ts',
    content: () => readTemplate('utils/user/get-user-initials.ts')
  },
  'user-get-user-sort-value': {
    relPath: 'utils/user/get-user-sort-value.ts',
    content: () => readTemplate('utils/user/get-user-sort-value.ts')
  },
  'user-get-users-page': {
    relPath: 'utils/user/get-users-page.ts',
    content: () => readTemplate('utils/user/get-users-page.ts')
  },
  'user-normalize-user-filter-value': {
    relPath: 'utils/user/normalize-user-filter-value.ts',
    content: () => readTemplate('utils/user/normalize-user-filter-value.ts')
  },
  'validation-file-validation-config': {
    relPath: 'utils/validation/file-validation-config.ts',
    content: () => readTemplate('utils/validation/file-validation-config.ts')
  },
  'validation-file-validation-error': {
    relPath: 'utils/validation/file-validation-error.ts',
    content: () => readTemplate('utils/validation/file-validation-error.ts')
  },
  'validation-file-validation-result': {
    relPath: 'utils/validation/file-validation-result.ts',
    content: () => readTemplate('utils/validation/file-validation-result.ts')
  },
  'validation-format-file-size': {
    relPath: 'utils/validation/format-file-size.ts',
    content: () => readTemplate('utils/validation/format-file-size.ts')
  },
  'validation-is-file-type-allowed': {
    relPath: 'utils/validation/is-file-type-allowed.ts',
    content: () => readTemplate('utils/validation/is-file-type-allowed.ts')
  },
  'validation-is-valid-url': {
    relPath: 'utils/validation/is-valid-url.ts',
    content: () => readTemplate('utils/validation/is-valid-url.ts')
  },
  'validation-slugify': {
    relPath: 'utils/validation/slugify.ts',
    content: () => readTemplate('utils/validation/slugify.ts')
  },
  'validation-validate-files': {
    relPath: 'utils/validation/validate-files.ts',
    content: () => readTemplate('utils/validation/validate-files.ts')
  },
  'webhook-create-webhook-event-id': {
    relPath: 'utils/webhook/create-webhook-event-id.ts',
    content: () => readTemplate('utils/webhook/create-webhook-event-id.ts')
  },
  'webhook-generate-webhook-secret': {
    relPath: 'utils/webhook/generate-webhook-secret.ts',
    content: () => readTemplate('utils/webhook/generate-webhook-secret.ts')
  },
  'webhook-sign-webhook-payload': {
    relPath: 'utils/webhook/sign-webhook-payload.ts',
    content: () => readTemplate('utils/webhook/sign-webhook-payload.ts')
  },
  'webhook-events-data': {
    relPath: 'data/webhook-events.ts',
    content: () => readTemplate('data/webhook-events.ts')
  },
  'dev-mode-code-block-height-utils': {
    relPath: 'utils/dev-mode/code-block-height.ts',
    content: () => readTemplate('utils/dev-mode/code-block-height.ts')
  },
  'lifecycle-hooks': {
    relPath: 'lib/lifecycle-hooks/index.ts',
    content: () => readTemplate('lib/lifecycle-hooks/index.ts'),
    dependencies: [
      'audit-register-action',
      'lifecycle-hooks-types',
      'lifecycle-hooks-registry',
      'lifecycle-hooks-runner',
      'lifecycle-hooks-register',
      'lifecycle-hooks-register-local'
    ]
  },
  'lifecycle-hooks-types': {
    relPath: 'lib/lifecycle-hooks/types.ts',
    content: () => readTemplate('lib/lifecycle-hooks/types.ts'),
    dependencies: ['audit-action-types']
  },
  'lifecycle-hooks-registry': {
    relPath: 'lib/lifecycle-hooks/registry.ts',
    content: () => readTemplate('lib/lifecycle-hooks/registry.ts')
  },
  'lifecycle-hooks-runner': {
    relPath: 'lib/lifecycle-hooks/runner.ts',
    content: () => readTemplate('lib/lifecycle-hooks/runner.ts')
  },
  'lifecycle-hooks-register': {
    relPath: 'lib/lifecycle-hooks/register.ts',
    content: () => readTemplate('lib/lifecycle-hooks/register.ts'),
    dependencies: ['webhooks-register-webhook-hooks']
  },
  'lifecycle-hooks-register-local': {
    relPath: 'lib/lifecycle-hooks/register.local.ts',
    content: () => readTemplate('lib/lifecycle-hooks/register.local.ts'),
    preserveExisting: true
  },
  'mailchimp-integration': {
    relPath: 'lib/actions/mailchimp/add-to-mailchimp-audience.ts',
    requiredIntegration: 'mailchimp',
    content: () => readIntegrationTemplate('mailchimp', 'actions/mailchimp.ts')
  },
  'auth-server': {
    relPath: 'lib/actions/auth/server.ts',
    content: () => readTemplate('lib/actions/auth/auth.ts')
  },
  'auth-client': {
    relPath: 'lib/actions/auth/client.ts',
    content: () => readTemplate('lib/actions/auth/client.ts')
  },
  'auth-session': {
    relPath: 'lib/actions/auth/session.ts',
    content: () => readTemplate('lib/actions/auth/middleware.ts'),
    dependencies: ['auth-roles-utils']
  },
  'admin-layout': {
    relPath: 'app/(admin)/admin/layout.tsx',
    content: () => readTemplate('pages/admin-layout.tsx'),
    base: 'cwd',
    dependencies: ['admin-globals', 'admin-providers', 'cn']
  },
  'auth-layout': {
    relPath: 'app/(admin)/admin/(authenticated)/layout.tsx',
    content: () => readTemplate('pages/authenticated-layout.tsx'),
    base: 'cwd',
    dependencies: ['auth-gate']
  },
  'auth-gate': {
    relPath: 'app/(admin)/admin/(authenticated)/auth-gate.tsx',
    content: () => readTemplate('pages/auth-gate.tsx'),
    base: 'cwd',
    dependencies: ['admin-sidebar', 'auth-session']
  },
  'account-layout': {
    relPath: 'app/(admin)/admin/(account)/layout.tsx',
    content: () => readTemplate('pages/account-layout.tsx'),
    base: 'cwd',
    dependencies: ['account-shell']
  },
  'account-shell': {
    relPath: 'app/(admin)/admin/(account)/account-shell.tsx',
    content: () => readTemplate('pages/account-shell.tsx'),
    base: 'cwd',
    dependencies: [
      'admin-sidebar',
      'app-admin',
      'auth-roles-utils',
      'auth-session',
      'minimal-account-shell'
    ]
  },
  'minimal-account-shell': {
    relPath: 'app/(admin)/admin/(account)/minimal-account-shell.tsx',
    content: () => readTemplate('pages/minimal-account-shell.tsx'),
    base: 'cwd',
    dependencies: ['admin-sign-out']
  },
  'profile-page': {
    relPath: 'app/(admin)/admin/(account)/profile/page.tsx',
    content: () => readTemplate('pages/profile/profile-page.tsx'),
    base: 'cwd',
    dependencies: ['profile-page-content', 'profile-page-skeleton']
  },
  'profile-page-content': {
    relPath: 'app/(admin)/admin/(account)/profile/profile-page-content.tsx',
    content: () => readTemplate('pages/profile/profile-page-content.tsx'),
    base: 'cwd',
    dependencies: ['auth-session', 'auth-types', 'page-header', 'profile-form']
  },
  'profile-page-skeleton': {
    relPath: 'app/(admin)/admin/(account)/profile/profile-page-skeleton.tsx',
    content: () => readTemplate('pages/profile/profile-page-skeleton.tsx'),
    base: 'cwd'
  },
  'profile-form': {
    relPath: 'app/(admin)/admin/(account)/profile/profile-form.tsx',
    content: () => readTemplate('pages/profile/profile-form.tsx'),
    base: 'cwd',
    dependencies: ['profile-action']
  },
  'login-page': {
    relPath: 'app/(admin)/admin/(auth)/login/page.tsx',
    content: () => readTemplate('pages/login-page.tsx'),
    base: 'cwd',
    dependencies: ['login-page-content', 'login-page-skeleton']
  },
  'login-page-content': {
    relPath: 'app/(admin)/admin/(auth)/login/login-page-content.tsx',
    content: () => readTemplate('pages/login-page-content.tsx'),
    base: 'cwd',
    dependencies: ['auth-roles-utils', 'auth-session', 'login-form']
  },
  'login-page-skeleton': {
    relPath: 'app/(admin)/admin/(auth)/login/login-page-skeleton.tsx',
    content: () => readTemplate('pages/login-page-skeleton.tsx'),
    base: 'cwd'
  },
  'login-form': {
    relPath: 'app/(admin)/admin/(auth)/login/login-form.tsx',
    content: () => readTemplate('pages/login-form.tsx'),
    base: 'cwd',
    dependencies: ['auth-roles-utils']
  },
  'forgot-password-page': {
    relPath: 'app/(admin)/admin/(auth)/forgot-password/page.tsx',
    content: () => readTemplate('pages/forgot-password-page.tsx'),
    base: 'cwd',
    dependencies: ['forgot-password-page-skeleton']
  },
  'forgot-password-page-skeleton': {
    relPath: 'app/(admin)/admin/(auth)/forgot-password/forgot-password-page-skeleton.tsx',
    content: () => readTemplate('pages/forgot-password-page-skeleton.tsx'),
    base: 'cwd'
  },
  'forgot-password-form': {
    relPath: 'app/(admin)/admin/(auth)/forgot-password/forgot-password-form.tsx',
    content: () => readTemplate('pages/forgot-password-form.tsx'),
    base: 'cwd'
  },
  'reset-password-page': {
    relPath: 'app/(admin)/admin/(auth)/reset-password/page.tsx',
    content: () => readTemplate('pages/reset-password-page.tsx'),
    base: 'cwd',
    dependencies: ['reset-password-page-skeleton']
  },
  'reset-password-page-skeleton': {
    relPath: 'app/(admin)/admin/(auth)/reset-password/reset-password-page-skeleton.tsx',
    content: () => readTemplate('pages/reset-password-page-skeleton.tsx'),
    base: 'cwd'
  },
  'reset-password-form': {
    relPath: 'app/(admin)/admin/(auth)/reset-password/reset-password-form.tsx',
    content: () => readTemplate('pages/reset-password-form.tsx'),
    base: 'cwd'
  },
  'users-page': {
    relPath: 'app/(admin)/admin/(authenticated)/users/page.tsx',
    content: () => readTemplate('pages/users/users-page.tsx'),
    base: 'cwd',
    dependencies: ['users-page-skeleton', 'users-page-content']
  },
  'users-page-skeleton': {
    relPath: 'app/(admin)/admin/(authenticated)/users/users-page-skeleton.tsx',
    content: () => readTemplate('pages/users/users-page-skeleton.tsx'),
    base: 'cwd'
  },
  'users-page-content': {
    relPath: 'app/(admin)/admin/(authenticated)/users/users-page-content.tsx',
    content: () => readTemplate('pages/users/users-page-content.tsx'),
    base: 'cwd',
    dependencies: ['users-columns', 'users-table', 'create-user-dialog', 'entity-filters-bar']
  },
  'users-columns': {
    relPath: 'app/(admin)/admin/(authenticated)/users/columns.tsx',
    content: () => readTemplate('pages/users/columns.tsx'),
    base: 'cwd',
    dependencies: ['user-utils', 'delete-user-dialog', 'edit-role-dialog', 'change-password-dialog']
  },
  'users-table': {
    relPath: 'app/(admin)/admin/(authenticated)/users/users-table.tsx',
    content: () => readTemplate('pages/users/users-table.tsx'),
    base: 'cwd',
    dependencies: ['use-table-utils', 'data-grid', 'data-table-pagination', 'user-utils']
  },
  'create-user-dialog': {
    relPath: 'app/(admin)/admin/(authenticated)/users/create-user-dialog.tsx',
    content: () => readTemplate('pages/users/create-user-dialog.tsx'),
    base: 'cwd'
  },
  'delete-user-dialog': {
    relPath: 'app/(admin)/admin/(authenticated)/users/delete-user-dialog.tsx',
    content: () => readTemplate('pages/users/delete-user-dialog.tsx'),
    base: 'cwd'
  },
  'edit-role-dialog': {
    relPath: 'app/(admin)/admin/(authenticated)/users/edit-role-dialog.tsx',
    content: () => readTemplate('pages/users/edit-role-dialog.tsx'),
    base: 'cwd'
  },
  'change-password-dialog': {
    relPath: 'app/(admin)/admin/(authenticated)/users/change-password-dialog.tsx',
    content: () => readTemplate('pages/users/change-password-dialog.tsx'),
    base: 'cwd',
    dependencies: ['profile-action', 'users-action']
  },
  'settings-layout': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/layout.tsx',
    content: () => readTemplate('pages/settings/settings-layout.tsx'),
    base: 'cwd',
    dependencies: ['admin-settings-sidebar']
  },
  'forms-settings-page': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/forms/page.tsx',
    content: () => readTemplate('pages/settings/forms/forms-settings-page.tsx'),
    base: 'cwd',
    dependencies: [
      'auth-types',
      'role-gate',
      'forms-settings-page-skeleton',
      'forms-settings-page-content'
    ]
  },
  'forms-settings-page-skeleton': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/forms/forms-settings-page-skeleton.tsx',
    content: () => readTemplate('pages/settings/forms/forms-settings-page-skeleton.tsx'),
    base: 'cwd'
  },
  'forms-settings-page-content': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/forms/forms-settings-page-content.tsx',
    content: () => readTemplate('pages/settings/forms/forms-settings-page-content.tsx'),
    base: 'cwd',
    dependencies: [
      'form-notifications-drawer',
      'forms-settings-columns',
      'forms-settings-table',
      'page-header',
      'use-webhooks'
    ]
  },
  'forms-settings-columns': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/forms/forms-settings-columns.tsx',
    content: () => readTemplate('pages/settings/forms/forms-settings-columns.tsx'),
    base: 'cwd'
  },
  'forms-settings-table': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/forms/forms-settings-table.tsx',
    content: () => readTemplate('pages/settings/forms/forms-settings-table.tsx'),
    base: 'cwd',
    dependencies: ['data-grid']
  },
  'form-notifications-drawer': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/forms/form-notifications-drawer.tsx',
    content: () => readTemplate('pages/settings/forms/form-notifications-drawer.tsx'),
    base: 'cwd',
    dependencies: [
      'button',
      'card',
      'drawer',
      'form',
      'form-settings-action',
      'scroll-area',
      'textarea'
    ]
  },
  'webhooks-page': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/page.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhooks-page.tsx'),
    base: 'cwd',
    dependencies: ['auth-types', 'role-gate', 'webhooks-page-skeleton', 'webhooks-page-content']
  },
  'webhooks-page-skeleton': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/webhooks-page-skeleton.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhooks-page-skeleton.tsx'),
    base: 'cwd'
  },
  'webhooks-page-content': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/webhooks-page-content.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhooks-page-content.tsx'),
    base: 'cwd',
    dependencies: [
      'webhooks-table',
      'webhooks-columns',
      'webhook-endpoint-dialog',
      'delete-webhook-dialog',
      'use-webhooks',
      'webhooks-action'
    ]
  },
  'webhooks-table': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/webhooks-table.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhooks-table.tsx'),
    base: 'cwd',
    dependencies: ['use-webhooks']
  },
  'webhooks-columns': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/webhooks-columns.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhooks-columns.tsx'),
    base: 'cwd',
    dependencies: ['webhook-actions', 'webhook-enabled-switch']
  },
  'webhook-actions': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/webhook-actions.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhook-actions.tsx'),
    base: 'cwd',
    dependencies: ['use-webhooks']
  },
  'webhook-enabled-switch': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/webhook-enabled-switch.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhook-enabled-switch.tsx'),
    base: 'cwd',
    dependencies: ['use-webhooks']
  },
  'webhook-endpoint-dialog': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/webhook-endpoint-dialog.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhook-endpoint-dialog.tsx'),
    base: 'cwd',
    dependencies: ['use-webhooks', 'webhooks-action']
  },
  'delete-webhook-dialog': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/delete-webhook-dialog.tsx',
    content: () => readTemplate('pages/settings/webhooks/delete-webhook-dialog.tsx'),
    base: 'cwd',
    dependencies: ['webhooks-action']
  },
  'webhooks-logs-page': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/logs/page.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhooks-logs-page.tsx'),
    base: 'cwd',
    dependencies: [
      'auth-types',
      'role-gate',
      'webhooks-logs-page-content',
      'webhooks-page-skeleton'
    ]
  },
  'webhooks-logs-page-content': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/webhooks-logs-page-content.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhooks-logs-page-content.tsx'),
    base: 'cwd',
    dependencies: ['webhooks-logs-table', 'webhooks-logs-columns', 'use-webhooks']
  },
  'webhooks-logs-table': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/webhooks-logs-table.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhooks-logs-table.tsx'),
    base: 'cwd',
    dependencies: ['use-webhooks']
  },
  'webhooks-logs-columns': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/webhooks/webhooks-logs-columns.tsx',
    content: () => readTemplate('pages/settings/webhooks/webhooks-logs-columns.tsx'),
    base: 'cwd',
    dependencies: ['webhooks-action']
  },
  'email-index': {
    relPath: 'lib/actions/email/index.ts',
    content: (context) =>
      context
        ? renderCoreEmailProviderIndex(context.config)
        : readTemplate('lib/actions/email/index.ts')
  },
  'email-provider': {
    relPath: 'lib/actions/email/provider.ts',
    content: (context) =>
      context
        ? renderCoreEmailProvider(context.config)
        : readTemplate('lib/actions/email/provider.ts')
  },
  'email-types': {
    relPath: 'lib/actions/email/types.ts',
    content: () => readTemplate('lib/actions/email/types.ts')
  },
  'email-is-delivery-configured': {
    relPath: 'lib/actions/email/is-email-delivery-configured.ts',
    content: () => readTemplate('lib/actions/email/is-email-delivery-configured.ts')
  },
  'email-send-email': {
    relPath: 'lib/actions/email/send-email.ts',
    content: () => readTemplate('lib/actions/email/send-email.ts')
  },
  'email-send-password-reset-email': {
    relPath: 'lib/actions/email/send-password-reset-email.ts',
    content: () => readTemplate('lib/actions/email/send-password-reset-email.ts')
  },
  'email-queued-form-email': {
    relPath: 'lib/actions/email/queued-form-email.ts',
    content: () => readTemplate('lib/actions/email/queued-form-email.ts'),
    dependencies: ['email-form-delivery-utils']
  },
  'email-send-form-emails': {
    relPath: 'lib/actions/email/send-form-emails.ts',
    content: () => readTemplate('lib/actions/email/send-form-emails.ts'),
    dependencies: ['email-form-delivery-utils']
  },
  'email-send-queued-email': {
    relPath: 'lib/actions/email/send-queued-email.ts',
    content: () => readTemplate('lib/actions/email/send-queued-email.ts'),
    dependencies: ['email-form-delivery-utils']
  },
  'email-none-provider': {
    relPath: 'lib/actions/email/none.ts',
    content: () => readTemplate('lib/actions/email/none.ts')
  },
  'email-resend-get-env-value': {
    relPath: 'lib/actions/resend/get-env-value.ts',
    requiredIntegration: 'resend',
    content: () => readIntegrationTemplate('resend', 'actions/get-env-value.ts')
  },
  'email-resend-get-from-address': {
    relPath: 'lib/actions/resend/get-from-address.ts',
    requiredIntegration: 'resend',
    content: () => readIntegrationTemplate('resend', 'actions/get-from-address.ts')
  },
  'email-resend-get-resend-client': {
    relPath: 'lib/actions/resend/get-resend-client.ts',
    requiredIntegration: 'resend',
    content: () => readIntegrationTemplate('resend', 'actions/get-resend-client.ts')
  },
  'email-resend-is-configured': {
    relPath: 'lib/actions/resend/is-configured.ts',
    requiredIntegration: 'resend',
    content: () => readIntegrationTemplate('resend', 'actions/is-configured.ts')
  },
  'email-resend-html-to-text': {
    relPath: 'lib/actions/resend/html-to-text.ts',
    requiredIntegration: 'resend',
    content: () => readIntegrationTemplate('resend', 'actions/html-to-text.ts')
  },
  'email-resend-send-email': {
    relPath: 'lib/actions/resend/send-email.ts',
    requiredIntegration: 'resend',
    content: () => readIntegrationTemplate('resend', 'actions/send-email.ts')
  },
  'email-resend-escape-html': {
    relPath: 'lib/actions/resend/escape-html.ts',
    requiredIntegration: 'resend',
    content: () => readIntegrationTemplate('resend', 'actions/escape-html.ts')
  },
  'email-resend-render-password-reset-email': {
    relPath: 'lib/actions/resend/render-password-reset-email.ts',
    requiredIntegration: 'resend',
    content: () => readIntegrationTemplate('resend', 'actions/render-password-reset-email.ts')
  },
  'email-resend-provider': {
    relPath: 'lib/actions/resend/provider.ts',
    requiredIntegration: 'resend',
    content: () => readIntegrationTemplate('resend', 'actions/provider.ts'),
    dependencies: [
      'email-resend-get-env-value',
      'email-resend-get-from-address',
      'email-resend-get-resend-client',
      'email-resend-is-configured',
      'email-resend-html-to-text',
      'email-resend-send-email',
      'email-resend-escape-html',
      'email-resend-render-password-reset-email'
    ]
  },
  'storage-index': {
    relPath: 'lib/actions/storage/index.ts',
    content: (context) =>
      context
        ? renderCoreStorageProviderIndex(context.config)
        : readTemplate('lib/actions/storage/index.ts')
  },
  'storage-provider': {
    relPath: 'lib/actions/storage/provider.ts',
    content: (context) =>
      context
        ? renderCoreStorageProvider(context.config)
        : readTemplate('lib/actions/storage/provider.ts')
  },
  'storage-types': {
    relPath: 'lib/actions/storage/types.ts',
    content: () => readTemplate('lib/actions/storage/types.ts')
  },
  'storage-save-upload': {
    relPath: 'lib/actions/storage/save-upload.ts',
    content: () => readTemplate('lib/actions/storage/save-upload.ts')
  },
  'storage-local-provider': {
    relPath: 'lib/actions/storage/local.ts',
    content: () => readTemplate('lib/actions/storage/local.ts'),
    dependencies: ['storage-local-utils']
  },
  'storage-r2-get-config': {
    relPath: 'lib/actions/r2/get-config.ts',
    requiredIntegration: 'r2',
    content: () => readIntegrationTemplate('r2', 'actions/get-config.ts')
  },
  'storage-r2-get-client': {
    relPath: 'lib/actions/r2/get-client.ts',
    requiredIntegration: 'r2',
    content: () => readIntegrationTemplate('r2', 'actions/get-client.ts')
  },
  'storage-r2-provider': {
    relPath: 'lib/actions/r2/provider.ts',
    requiredIntegration: 'r2',
    content: () => readIntegrationTemplate('r2', 'actions/provider.ts'),
    dependencies: ['storage-r2-get-config', 'storage-r2-get-client']
  },
  'storage-vercel-blob-get-token': {
    relPath: 'lib/actions/vercel-blob/get-token.ts',
    requiredIntegration: 'vercel-blob',
    content: () => readIntegrationTemplate('vercel-blob', 'actions/get-token.ts')
  },
  'storage-vercel-blob-provider': {
    relPath: 'lib/actions/vercel-blob/provider.ts',
    requiredIntegration: 'vercel-blob',
    content: () => readIntegrationTemplate('vercel-blob', 'actions/provider.ts'),
    dependencies: ['storage-vercel-blob-get-token']
  },
  'storage-railway-bucket-get-config': {
    relPath: 'lib/actions/railway-bucket/get-config.ts',
    requiredIntegration: 'railway-bucket',
    content: () => readIntegrationTemplate('railway-bucket', 'actions/get-config.ts')
  },
  'storage-railway-bucket-stable-read-url': {
    relPath: 'lib/actions/railway-bucket/stable-read-url.ts',
    requiredIntegration: 'railway-bucket',
    content: () => readIntegrationTemplate('railway-bucket', 'actions/stable-read-url.ts')
  },
  'storage-railway-bucket-get-client': {
    relPath: 'lib/actions/railway-bucket/get-client.ts',
    requiredIntegration: 'railway-bucket',
    content: () => readIntegrationTemplate('railway-bucket', 'actions/get-client.ts')
  },
  'storage-railway-bucket-provider': {
    relPath: 'lib/actions/railway-bucket/provider.ts',
    requiredIntegration: 'railway-bucket',
    content: () => readIntegrationTemplate('railway-bucket', 'actions/provider.ts'),
    dependencies: [
      'storage-types',
      'storage-read-route',
      'storage-railway-bucket-get-config',
      'storage-railway-bucket-stable-read-url',
      'storage-railway-bucket-get-client'
    ]
  },
  'form-settings-action': {
    relPath: 'lib/actions/forms/index.ts',
    content: () => readTemplate('lib/actions/forms/index.ts'),
    dependencies: [
      'form-settings-types',
      'get-form-settings-action',
      'upsert-form-settings-action',
      'get-all-form-settings-action'
    ]
  },
  'form-settings-types': {
    relPath: 'lib/actions/forms/types.ts',
    content: () => readTemplate('lib/actions/forms/types.ts')
  },
  'get-form-settings-action': {
    relPath: 'lib/actions/forms/get-form-settings.ts',
    content: () => readTemplate('lib/actions/forms/get-form-settings.ts'),
    dependencies: ['form-settings-types']
  },
  'upsert-form-settings-action': {
    relPath: 'lib/actions/forms/upsert-form-settings.ts',
    content: () => readTemplate('lib/actions/forms/upsert-form-settings.ts'),
    dependencies: ['form-settings-types']
  },
  'get-all-form-settings-action': {
    relPath: 'lib/actions/forms/get-all-form-settings.ts',
    content: () => readTemplate('lib/actions/forms/get-all-form-settings.ts'),
    dependencies: ['form-settings-types']
  },
  'webhooks-action': {
    relPath: 'lib/actions/webhooks/index.ts',
    content: () => readTemplate('lib/actions/webhooks/index.ts'),
    dependencies: [
      'webhooks-action-types',
      'internal-get-active-endpoints-action',
      'webhooks-deliver-to-endpoint',
      'webhooks-deliver-webhook-event',
      'webhooks-emit-webhook-event',
      'webhooks-entity-event-payload',
      'webhooks-prune-webhook-deliveries',
      'webhooks-read-webhook-subscriptions',
      'webhooks-record-webhook-delivery',
      'webhooks-register-webhook-hooks',
      'get-webhooks-action',
      'create-webhook-action',
      'update-webhook-action',
      'delete-webhook-action',
      'get-webhook-secret-action',
      'regenerate-webhook-secret-action',
      'get-webhook-subscriptions-action',
      'send-test-webhook-action',
      'get-webhook-deliveries-action'
    ]
  },
  'webhooks-action-types': {
    relPath: 'lib/actions/webhooks/types.ts',
    content: () => readTemplate('lib/actions/webhooks/types.ts')
  },
  'internal-get-active-endpoints-action': {
    relPath: 'lib/actions/webhooks/internal-get-active-endpoints.ts',
    content: () => readTemplate('lib/actions/webhooks/internal-get-active-endpoints.ts')
  },
  'webhooks-deliver-to-endpoint': {
    relPath: 'lib/actions/webhooks/deliver-to-endpoint.ts',
    content: () => readTemplate('lib/actions/webhooks/deliver-to-endpoint.ts'),
    dependencies: [
      'internal-get-active-endpoints-action',
      'webhooks-record-webhook-delivery',
      'webhook-sign-webhook-payload'
    ]
  },
  'webhooks-deliver-webhook-event': {
    relPath: 'lib/actions/webhooks/deliver-webhook-event.ts',
    content: () => readTemplate('lib/actions/webhooks/deliver-webhook-event.ts'),
    dependencies: ['webhooks-deliver-to-endpoint', 'internal-get-active-endpoints-action']
  },
  'webhooks-emit-webhook-event': {
    relPath: 'lib/actions/webhooks/emit-webhook-event.ts',
    content: () => readTemplate('lib/actions/webhooks/emit-webhook-event.ts'),
    dependencies: ['webhooks-deliver-webhook-event', 'webhook-create-webhook-event-id']
  },
  'webhooks-entity-event-payload': {
    relPath: 'lib/actions/webhooks/entity-event-payload.ts',
    content: () => readTemplate('lib/actions/webhooks/entity-event-payload.ts'),
    dependencies: ['lifecycle-hooks-types']
  },
  'webhooks-prune-webhook-deliveries': {
    relPath: 'lib/actions/webhooks/prune-webhook-deliveries.ts',
    content: () => readTemplate('lib/actions/webhooks/prune-webhook-deliveries.ts')
  },
  'webhooks-read-webhook-subscriptions': {
    relPath: 'lib/actions/webhooks/read-webhook-subscriptions.ts',
    content: () => readTemplate('lib/actions/webhooks/read-webhook-subscriptions.ts'),
    dependencies: ['webhooks-action-types']
  },
  'webhooks-record-webhook-delivery': {
    relPath: 'lib/actions/webhooks/record-webhook-delivery.ts',
    content: () => readTemplate('lib/actions/webhooks/record-webhook-delivery.ts'),
    dependencies: ['webhooks-prune-webhook-deliveries']
  },
  'webhooks-register-webhook-hooks': {
    relPath: 'lib/actions/webhooks/register-webhook-hooks.ts',
    content: () => readTemplate('lib/actions/webhooks/register-webhook-hooks.ts'),
    dependencies: [
      'lifecycle-hooks-types',
      'lifecycle-hooks-registry',
      'webhooks-emit-webhook-event',
      'webhooks-entity-event-payload',
      'webhook-events-data'
    ]
  },
  'get-webhooks-action': {
    relPath: 'lib/actions/webhooks/get-webhooks.ts',
    content: () => readTemplate('lib/actions/webhooks/get-webhooks.ts'),
    dependencies: ['webhooks-action-types']
  },
  'create-webhook-action': {
    relPath: 'lib/actions/webhooks/create-webhook.ts',
    content: () => readTemplate('lib/actions/webhooks/create-webhook.ts'),
    dependencies: ['webhooks-action-types', 'webhook-generate-webhook-secret']
  },
  'update-webhook-action': {
    relPath: 'lib/actions/webhooks/update-webhook.ts',
    content: () => readTemplate('lib/actions/webhooks/update-webhook.ts'),
    dependencies: ['webhooks-action-types']
  },
  'delete-webhook-action': {
    relPath: 'lib/actions/webhooks/delete-webhook.ts',
    content: () => readTemplate('lib/actions/webhooks/delete-webhook.ts'),
    dependencies: ['webhooks-action-types']
  },
  'get-webhook-secret-action': {
    relPath: 'lib/actions/webhooks/get-webhook-secret.ts',
    content: () => readTemplate('lib/actions/webhooks/get-webhook-secret.ts'),
    dependencies: ['webhooks-action-types']
  },
  'regenerate-webhook-secret-action': {
    relPath: 'lib/actions/webhooks/regenerate-webhook-secret.ts',
    content: () => readTemplate('lib/actions/webhooks/regenerate-webhook-secret.ts'),
    dependencies: ['webhooks-action-types', 'webhook-generate-webhook-secret']
  },
  'get-webhook-subscriptions-action': {
    relPath: 'lib/actions/webhooks/get-webhook-subscriptions.ts',
    content: () => readTemplate('lib/actions/webhooks/get-webhook-subscriptions.ts'),
    dependencies: ['webhooks-action-types', 'webhooks-read-webhook-subscriptions']
  },
  'send-test-webhook-action': {
    relPath: 'lib/actions/webhooks/send-test-webhook.ts',
    content: () => readTemplate('lib/actions/webhooks/send-test-webhook.ts'),
    dependencies: [
      'webhooks-action-types',
      'webhook-create-webhook-event-id',
      'webhook-sign-webhook-payload',
      'webhooks-record-webhook-delivery'
    ]
  },
  'get-webhook-deliveries-action': {
    relPath: 'lib/actions/webhooks/get-webhook-deliveries.ts',
    content: () => readTemplate('lib/actions/webhooks/get-webhook-deliveries.ts'),
    dependencies: ['webhooks-action-types']
  },
  'entity-versions-action': {
    relPath: 'lib/actions/entity-versions/index.ts',
    content: () => readTemplate('lib/actions/entity-versions/index.ts'),
    dependencies: [
      'entity-versions-types',
      'get-entity-versions-action',
      'internal-create-entity-version-action',
      'internal-delete-entity-versions-action'
    ]
  },
  'entity-versions-types': {
    relPath: 'lib/actions/entity-versions/types.ts',
    content: () => readTemplate('lib/actions/entity-versions/types.ts')
  },
  'get-entity-versions-action': {
    relPath: 'lib/actions/entity-versions/get-entity-versions.ts',
    content: () => readTemplate('lib/actions/entity-versions/get-entity-versions.ts'),
    dependencies: ['entity-versions-types']
  },
  'internal-create-entity-version-action': {
    relPath: 'lib/actions/entity-versions/internal-create-entity-version.ts',
    content: () => readTemplate('lib/actions/entity-versions/internal-create-entity-version.ts'),
    dependencies: ['entity-versions-types']
  },
  'internal-delete-entity-versions-action': {
    relPath: 'lib/actions/entity-versions/internal-delete-entity-versions.ts',
    content: () => readTemplate('lib/actions/entity-versions/internal-delete-entity-versions.ts')
  },
  'audit-action': {
    relPath: 'lib/actions/audit/index.ts',
    content: () => readTemplate('lib/actions/audit/index.ts'),
    dependencies: [
      'audit-action-types',
      'audit-cleanup-action',
      'audit-get-action',
      'audit-record-event-action',
      'audit-register-action',
      'audit-request-metadata-action'
    ]
  },
  'audit-action-types': {
    relPath: 'lib/actions/audit/types.ts',
    content: () => readTemplate('lib/actions/audit/types.ts')
  },
  'audit-clamp-page': {
    relPath: 'lib/actions/audit/clamp-page.ts',
    content: () => readTemplate('lib/actions/audit/clamp-page.ts')
  },
  'audit-first-header-value': {
    relPath: 'lib/actions/audit/first-header-value.ts',
    content: () => readTemplate('lib/actions/audit/first-header-value.ts')
  },
  'audit-get-audit-log-order-by': {
    relPath: 'lib/actions/audit/get-audit-log-order-by.ts',
    content: () => readTemplate('lib/actions/audit/get-audit-log-order-by.ts')
  },
  'audit-get-audit-request-metadata': {
    relPath: 'lib/actions/audit/get-audit-request-metadata.ts',
    content: () => readTemplate('lib/actions/audit/get-audit-request-metadata.ts')
  },
  'audit-get-retention-days': {
    relPath: 'lib/actions/audit/get-retention-days.ts',
    content: () => readTemplate('lib/actions/audit/get-retention-days.ts')
  },
  'audit-max-ip-length': {
    relPath: 'lib/actions/audit/max-ip-length.ts',
    content: () => readTemplate('lib/actions/audit/max-ip-length.ts')
  },
  'audit-max-user-agent-length': {
    relPath: 'lib/actions/audit/max-user-agent-length.ts',
    content: () => readTemplate('lib/actions/audit/max-user-agent-length.ts')
  },
  'audit-normalize-changed-fields': {
    relPath: 'lib/actions/audit/normalize-changed-fields.ts',
    content: () => readTemplate('lib/actions/audit/normalize-changed-fields.ts')
  },
  'audit-normalize-page-size': {
    relPath: 'lib/actions/audit/normalize-page-size.ts',
    content: () => readTemplate('lib/actions/audit/normalize-page-size.ts')
  },
  'audit-query-audit-logs': {
    relPath: 'lib/actions/audit/query-audit-logs.ts',
    content: () => readTemplate('lib/actions/audit/query-audit-logs.ts')
  },
  'audit-record-event-action': {
    relPath: 'lib/actions/audit/record-audit-event.ts',
    content: () => readTemplate('lib/actions/audit/record-audit-event.ts'),
    dependencies: ['audit-action-types']
  },
  'audit-register-action': {
    relPath: 'lib/actions/audit/register.ts',
    content: () => readTemplate('lib/actions/audit/register.ts'),
    dependencies: ['audit-record-event-action']
  },
  'audit-get-action': {
    relPath: 'lib/actions/audit/get-audit-logs.ts',
    content: () => readTemplate('lib/actions/audit/get-audit-logs.ts'),
    dependencies: ['audit-action-types']
  },
  'audit-cleanup-action': {
    relPath: 'lib/actions/audit/cleanup-audit-logs.ts',
    content: () => readTemplate('lib/actions/audit/cleanup-audit-logs.ts'),
    dependencies: ['audit-action-types', 'audit-request-metadata-action', 'lifecycle-hooks']
  },
  'upload-action': {
    relPath: 'lib/actions/upload/index.ts',
    content: () => readTemplate('lib/actions/upload/index.ts'),
    dependencies: [
      'upload-action-types',
      'upload-remote-utils',
      'upload-files-action',
      'upload-file-action',
      'upload-media-from-url-action',
      'upload-image-from-url-action'
    ]
  },
  'upload-action-types': {
    relPath: 'lib/actions/upload/types.ts',
    content: () => readTemplate('lib/actions/upload/types.ts')
  },
  'upload-files-action': {
    relPath: 'lib/actions/upload/upload-files.ts',
    content: () => readTemplate('lib/actions/upload/upload-files.ts')
  },
  'upload-file-action': {
    relPath: 'lib/actions/upload/upload-file.ts',
    content: () => readTemplate('lib/actions/upload/upload-file.ts')
  },
  'upload-media-from-url-action': {
    relPath: 'lib/actions/upload/upload-media-from-url.ts',
    content: () => readTemplate('lib/actions/upload/upload-media-from-url.ts')
  },
  'upload-image-from-url-action': {
    relPath: 'lib/actions/upload/upload-image-from-url.ts',
    content: () => readTemplate('lib/actions/upload/upload-image-from-url.ts')
  },
  'users-action': {
    relPath: 'lib/actions/users/index.ts',
    content: () => readTemplate('lib/actions/users/index.ts'),
    dependencies: [
      'users-action-types',
      'change-user-password-action',
      'create-user-action',
      'get-users-action',
      'update-user-role-action',
      'delete-user-action'
    ]
  },
  'users-action-types': {
    relPath: 'lib/actions/users/types.ts',
    content: () => readTemplate('lib/actions/users/types.ts')
  },
  'change-user-password-action': {
    relPath: 'lib/actions/users/change-user-password.ts',
    content: () => readTemplate('lib/actions/users/change-user-password.ts'),
    dependencies: ['audit-request-metadata-action', 'lifecycle-hooks', 'users-action-types']
  },
  'create-user-action': {
    relPath: 'lib/actions/users/create-user.ts',
    content: () => readTemplate('lib/actions/users/create-user.ts'),
    dependencies: [
      'audit-request-metadata-action',
      'audit-utils',
      'lifecycle-hooks',
      'users-action-types'
    ]
  },
  'get-users-action': {
    relPath: 'lib/actions/users/get-users.ts',
    content: () => readTemplate('lib/actions/users/get-users.ts'),
    dependencies: ['users-action-types']
  },
  'update-user-role-action': {
    relPath: 'lib/actions/users/update-user-role.ts',
    content: () => readTemplate('lib/actions/users/update-user-role.ts'),
    dependencies: ['audit-request-metadata-action', 'lifecycle-hooks', 'users-action-types']
  },
  'delete-user-action': {
    relPath: 'lib/actions/users/delete-user.ts',
    content: () => readTemplate('lib/actions/users/delete-user.ts'),
    dependencies: ['audit-request-metadata-action', 'lifecycle-hooks', 'users-action-types']
  },
  'media-action': {
    relPath: 'lib/actions/media/index.ts',
    content: () => readTemplate('lib/actions/media/index.ts'),
    dependencies: [
      'media-action-types',
      'media-query-utils',
      'get-media-action',
      'get-media-by-id-action',
      'get-media-by-ids-action',
      'create-media-action',
      'update-media-action',
      'delete-media-action',
      'delete-media-bulk-action'
    ]
  },
  'media-action-types': {
    relPath: 'lib/actions/media/types.ts',
    content: () => readTemplate('lib/actions/media/types.ts')
  },
  'get-media-action': {
    relPath: 'lib/actions/media/get-media.ts',
    content: () => readTemplate('lib/actions/media/get-media.ts'),
    dependencies: ['media-action-types']
  },
  'get-media-by-id-action': {
    relPath: 'lib/actions/media/get-media-by-id.ts',
    content: () => readTemplate('lib/actions/media/get-media-by-id.ts'),
    dependencies: ['media-action-types']
  },
  'get-media-by-ids-action': {
    relPath: 'lib/actions/media/get-media-by-ids.ts',
    content: () => readTemplate('lib/actions/media/get-media-by-ids.ts'),
    dependencies: ['media-action-types']
  },
  'create-media-action': {
    relPath: 'lib/actions/media/create-media.ts',
    content: () => readTemplate('lib/actions/media/create-media.ts'),
    dependencies: [
      'audit-request-metadata-action',
      'audit-utils',
      'lifecycle-hooks',
      'media-action-types'
    ]
  },
  'update-media-action': {
    relPath: 'lib/actions/media/update-media.ts',
    content: () => readTemplate('lib/actions/media/update-media.ts'),
    dependencies: [
      'audit-request-metadata-action',
      'audit-utils',
      'lifecycle-hooks',
      'media-action-types'
    ]
  },
  'delete-media-action': {
    relPath: 'lib/actions/media/delete-media.ts',
    content: () => readTemplate('lib/actions/media/delete-media.ts'),
    dependencies: ['audit-request-metadata-action', 'lifecycle-hooks', 'media-action-types']
  },
  'delete-media-bulk-action': {
    relPath: 'lib/actions/media/delete-media-bulk.ts',
    content: () => readTemplate('lib/actions/media/delete-media-bulk.ts'),
    dependencies: ['audit-request-metadata-action', 'lifecycle-hooks', 'media-action-types']
  },
  'profile-action': {
    relPath: 'lib/actions/profile/index.ts',
    content: () => readTemplate('lib/actions/profile/index.ts'),
    dependencies: [
      'profile-action-types',
      'change-own-password-action',
      'is-email-configured-action',
      'invalidate-users-cache-action',
      'update-email-action'
    ]
  },
  'profile-action-types': {
    relPath: 'lib/actions/profile/types.ts',
    content: () => readTemplate('lib/actions/profile/types.ts')
  },
  'change-own-password-action': {
    relPath: 'lib/actions/profile/change-own-password.ts',
    content: () => readTemplate('lib/actions/profile/change-own-password.ts'),
    dependencies: [
      'audit-request-metadata-action',
      'lifecycle-hooks',
      'profile-action-types',
      'invalidate-users-cache-action'
    ]
  },
  'is-email-configured-action': {
    relPath: 'lib/actions/profile/is-email-configured.ts',
    content: () => readTemplate('lib/actions/profile/is-email-configured.ts')
  },
  'invalidate-users-cache-action': {
    relPath: 'lib/actions/profile/invalidate-users-cache.ts',
    content: () => readTemplate('lib/actions/profile/invalidate-users-cache.ts'),
    dependencies: ['users-action-types']
  },
  'update-email-action': {
    relPath: 'lib/actions/profile/update-email.ts',
    content: () => readTemplate('lib/actions/profile/update-email.ts'),
    dependencies: [
      'audit-request-metadata-action',
      'lifecycle-hooks',
      'profile-action-types',
      'invalidate-users-cache-action',
      'users-action-types'
    ]
  },
  'auth-route': {
    relPath: 'app/(admin)/api/admin/auth/[...all]/route.ts',
    content: () => readTemplate('api/auth-route.ts'),
    base: 'cwd',
    dependencies: ['auth-server']
  },
  'upload-route': {
    relPath: 'app/(admin)/api/admin/upload/route.ts',
    content: () => readTemplate('api/upload-route.ts'),
    base: 'cwd',
    dependencies: ['media-action-types']
  },
  'media-page': {
    relPath: 'app/(admin)/admin/(authenticated)/media/page.tsx',
    content: () => readTemplate('pages/media/media-page.tsx'),
    base: 'cwd',
    dependencies: ['media-page-skeleton']
  },
  'media-page-skeleton': {
    relPath: 'app/(admin)/admin/(authenticated)/media/media-page-skeleton.tsx',
    content: () => readTemplate('pages/media/media-page-skeleton.tsx'),
    base: 'cwd'
  },
  'storage-read-route': {
    relPath: (context) => `${context.config.paths.api}/storage/[key]/route.ts`,
    content: () => readTemplate('api/storage-read-route.ts'),
    base: 'cwd',
    dependencies: ['storage-types', 'storage-decode-storage-key', 'storage-storage-error']
  },
  'media-page-content': {
    relPath: 'app/(admin)/admin/(authenticated)/media/media-page-content.tsx',
    content: () => readTemplate('pages/media/media-page-content.tsx'),
    base: 'cwd',
    dependencies: ['use-media', 'media-grid-pagination']
  },
  'audit-log-page': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/audit-log/page.tsx',
    content: () => readTemplate('pages/settings/audit-log/audit-log-page.tsx'),
    base: 'cwd',
    dependencies: ['auth-types', 'role-gate', 'audit-log-page-content', 'audit-log-page-skeleton']
  },
  'audit-log-page-skeleton': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/audit-log/audit-log-page-skeleton.tsx',
    content: () => readTemplate('pages/settings/audit-log/audit-log-page-skeleton.tsx'),
    base: 'cwd',
    dependencies: ['page-header', 'skeleton']
  },
  'audit-log-page-content': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/audit-log/audit-log-page-content.tsx',
    content: () => readTemplate('pages/settings/audit-log/audit-log-page-content.tsx'),
    base: 'cwd',
    dependencies: [
      'audit-log-columns',
      'audit-log-detail-drawer',
      'audit-log-table',
      'entity-filters-bar',
      'page-header'
    ]
  },
  'audit-log-table': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/audit-log/audit-log-table.tsx',
    content: () => readTemplate('pages/settings/audit-log/audit-log-table.tsx'),
    base: 'cwd',
    dependencies: ['data-grid', 'data-table-pagination', 'use-audit-log', 'use-table-utils']
  },
  'audit-log-columns': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/audit-log/audit-log-columns.tsx',
    content: () => readTemplate('pages/settings/audit-log/audit-log-columns.tsx'),
    base: 'cwd',
    dependencies: ['badge', 'button', 'sort-indicator']
  },
  'audit-log-detail-drawer': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/audit-log/audit-log-detail-drawer.tsx',
    content: () => readTemplate('pages/settings/audit-log/audit-log-detail-drawer.tsx'),
    base: 'cwd',
    dependencies: ['audit-log-detail-row', 'badge', 'drawer', 'scroll-area']
  },
  'audit-log-detail-row': {
    relPath: 'app/(admin)/admin/(authenticated)/settings/audit-log/audit-log-detail-row.tsx',
    content: () => readTemplate('pages/settings/audit-log/audit-log-detail-row.tsx'),
    base: 'cwd'
  }
}
